import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdvancedTargeting from './AdvancedTargeting';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { getBranding, brandMark } from '../config/branding';
import { useTranslation, Trans } from 'react-i18next';
import i18n, { targetingLabel as tv, currentLocale } from '../i18n';
import '../styles/group-creatives.css';
import '../styles/sense-max.css';

// Canonical geo entity: one search (Sense-backed), resolved to each platform's
// own geo id at launch. country/level/canonical_name drive that resolution.
interface Geo { id: string; name: string; type: string; country?: string; level?: string; canonical_name?: string; }

/**
 * Sense Max — multi-platform campaign constructor (Phase 1).
 *
 * Mirrors the normal campaign editor (EditCampaignPage) EXACTLY — same layout
 * (left Campaign Details panel + right Edit Ad Group panel) and the same CSS
 * classes — but drops the platform-specific blocks that conflict across
 * platforms (Inventory, Viewability & Brand Safety, Remarketing, DV-only
 * Advanced bits). Creatives are reworked later. Local state only; nothing
 * is pushed.
 */

const DAYS = [
  { key: 'MONDAY', label: 'Mon', weekend: false }, { key: 'TUESDAY', label: 'Tue', weekend: false },
  { key: 'WEDNESDAY', label: 'Wed', weekend: false }, { key: 'THURSDAY', label: 'Thu', weekend: false },
  { key: 'FRIDAY', label: 'Fri', weekend: false }, { key: 'SATURDAY', label: 'Sat', weekend: true },
  { key: 'SUNDAY', label: 'Sun', weekend: true },
];
const ALL_DAYS = DAYS.map(d => d.key);
const ALL_CELLS = ALL_DAYS.flatMap(d => Array.from({ length: 24 }, (_, h) => `${d}-${h}`));
const AGE_RANGES = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const GENDERS = ['Male', 'Female'];
const PARENTAL = ['Parent', 'Not a Parent', 'Unknown'];
// Sense Max does not sell CTV, so it is not offered here — the regular Sense
// campaign form keeps its own, fuller list.
const DEVICES = ['Computer', 'Smart Phone', 'Tablet'];
// Where the ad may run: in a browser or inside an app. Only Sense can enforce it
// per-impression; the constructor says so rather than pretending the switch
// reaches every channel.
const PLACEMENT_ENVS = ['Web', 'App'];

const toggle = (arr: string[], v: string) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
const numOnly = (s: string) => s.replace(/\D/g, '');
const decimalOnly = (s: string) => s.replace(/[^\d.,]/g, '');

// ── Creative pre-check ──────────────────────────────────────────────────────────
const RATIO_VAL: Record<string, number> = {
  '1.91:1': 1.91, '1:1': 1, '4:5': 0.8, '9:16': 0.5625, '16:9': 1.7778, '4:1': 4, '3:4': 0.75,
};
// One aspect-ratio tolerance used EVERYWHERE (classify, fit, precheck) so a file
// accepted into a bucket also passes the per-format check. Real creatives are
// rarely pixel-exact (WhatsApp recompresses, exports vary); the supported ratios
// sit ≥0.2 apart, so this is generous without cross-matching. w/h ratio units.
const RATIO_TOL = 0.06;
const ratioMatches = (ar: number, ratios?: string[]) =>
  (ratios || []).some(r => RATIO_VAL[r] != null && Math.abs(ar - RATIO_VAL[r]) < RATIO_TOL);

// Programmatic display slots need EXACT IAB pixel sizes — a creative can't be resized to fit
// a slot, so banners must match a listed size pixel-for-pixel. When the RATIO is right but the
// pixels aren't (a 2× export or off-size), name the exact size the client should re-export to,
// so the rejection is actionable instead of a bare "no matching format".
const nearestExactSize = (ar: number, sizeLists: (string[] | undefined)[]): string | null => {
  for (const sizes of sizeLists) {
    for (const s of sizes || []) {
      const [w, h] = s.split('x').map(Number);
      if (w && h && Math.abs(ar - w / h) / (w / h) < 0.03) return `${w}×${h}`;
    }
  }
  return null;
};
const sizeLabel = (mb: number) => mb < 1 ? `${Math.round(mb * 1000)}KB` : `${mb}MB`;
function imgDims(file: File): Promise<{ w: number; h: number }> {
  return new Promise(res => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => { res({ w: im.naturalWidth, h: im.naturalHeight }); URL.revokeObjectURL(url); };
    im.onerror = () => { res({ w: 0, h: 0 }); URL.revokeObjectURL(url); };
    im.src = url;
  });
}
function vidMeta(file: File): Promise<{ dur: number }> {
  return new Promise(res => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => { res({ dur: v.duration }); URL.revokeObjectURL(url); };
    v.onerror = () => { res({ dur: 0 }); URL.revokeObjectURL(url); };
    v.src = url;
  });
}
// HTML5 ZIP checks — SAME logic as the normal creative upload (CreativeUploadPage
// validateHtml5): index.html present, clickTag variable, hardcoded-URL, ≤100 files.
async function zipIssues(file: File): Promise<string[]> {
  const out: string[] = [];
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(file);
    const names = Object.keys(zip.files).filter(f => !f.startsWith('__MACOSX'));
    if (names.length > 100) out.push(i18n.t('max.zipTooManyFiles', { n: names.length }));
    const hasIndex = names.some(f => f.toLowerCase() === 'index.html' || f.toLowerCase().endsWith('/index.html'));
    if (!hasIndex) out.push(i18n.t('max.zipNoIndex'));
    const htmls = names.filter(f => f.toLowerCase().endsWith('.html'));
    let hasClickTag = false, hardcoded = false;
    for (const h of htmls) {
      const c = await zip.files[h].async('string');
      if (/var\s+clickTag\s*=/i.test(c) || /window\.clickTag/i.test(c)) hasClickTag = true;
      if (/onclick\s*=\s*["']window\.open\s*\(\s*["']https?:\/\//i.test(c)) hardcoded = true;
    }
    if (!hasClickTag) out.push(i18n.t('max.zipNoClickTag'));
    if (hardcoded) out.push(i18n.t('max.zipHardcoded'));
  } catch {
    out.push(i18n.t('max.zipParseFailed'));
  }
  return out;
}

// Render an HTML5 ZIP banner live: unzip, blob-URL every asset, rewrite the HTML
// (and CSS) references to those blobs, return a self-contained srcdoc + ad size.
async function buildHtml5Srcdoc(file: File): Promise<{ srcdoc: string; w: number; h: number } | null> {
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(file);
    const names = Object.keys(zip.files).filter(n => !zip.files[n].dir && !n.startsWith('__MACOSX'));
    const htmlName = names.find(n => /(^|\/)index\.html?$/i.test(n)) || names.find(n => /\.html?$/i.test(n));
    if (!htmlName) return null;

    const urlMap: Record<string, string> = {};
    const textFiles: string[] = []; // css/js rewritten after binaries are mapped
    for (const n of names) {
      if (n === htmlName) continue;
      if (/\.(css|js)$/i.test(n)) { textFiles.push(n); continue; }
      urlMap[n] = URL.createObjectURL(await zip.files[n].async('blob'));
    }
    // Literal path → blob replacement: catches attributes, url(), and JS string paths.
    const applyMap = (text: string) => {
      let out = text;
      for (const [path, url] of Object.entries(urlMap)) {
        const base = path.split('/').pop() || path;
        for (const v of Array.from(new Set([path, './' + path, base]))) {
          if (!v) continue;
          out = out.split(`"${v}"`).join(`"${url}"`)
                   .split(`'${v}'`).join(`'${url}'`)
                   .split(`(${v})`).join(`(${url})`);
        }
      }
      return out;
    };
    for (const n of textFiles) {
      const type = /\.css$/i.test(n) ? 'text/css' : 'text/javascript';
      const content = applyMap(await zip.files[n].async('string'));
      urlMap[n] = URL.createObjectURL(new Blob([content], { type }));
    }
    let html = await zip.files[htmlName].async('string');
    let w = 0, h = 0;
    const meta = html.match(/name=["']ad\.size["']\s+content=["']width=(\d+),\s*height=(\d+)/i);
    if (meta) { w = +meta[1]; h = +meta[2]; }
    if (!w) { const fm = file.name.match(/(\d{2,4})[x_](\d{2,4})/); if (fm) { w = +fm[1]; h = +fm[2]; } }
    html = applyMap(html);
    return { srcdoc: html, w: w || 300, h: h || 250 };
  } catch { return null; }
}

async function precheckFile(file: File, a: any): Promise<string[]> {
  const issues: string[] = [];
  const ext = (file.name.split('.').pop() || '').toLowerCase().replace(/^jpeg$/, 'jpg');
  if (a.file_types?.length && !a.file_types.includes(ext)) issues.push(i18n.t('max.reasonExtNotAllowed', { ext }));
  if (a.max_size_mb != null && file.size / 1e6 > a.max_size_mb) issues.push(i18n.t('max.reasonOverSize', { size: sizeLabel(a.max_size_mb) }));
  if (file.type.startsWith('image/')) {
    const { w, h } = await imgDims(file);
    if (w && h) {
      const sizes: string[] = a.sizes_px || [];
      const ratios: string[] = a.ratios || [];
      const ratioOk = ratioMatches(w / h, ratios);
      const sizeOk = sizes.includes(`${w}x${h}`);
      if (sizes.length && !sizeOk && !ratioOk) issues.push(i18n.t('max.reasonSizeOff', { w, h }));
      else if (!sizes.length && ratios.length && !ratioOk) issues.push(i18n.t('max.reasonRatioOff', { ratio: (w / h).toFixed(2) }));
    }
  } else if (file.type.startsWith('video/')) {
    const { dur } = await vidMeta(file);
    if (dur) {
      if (a.dur_max != null && dur > a.dur_max + 0.5) issues.push(i18n.t('max.reasonOverDur', { sec: a.dur_max }));
      if (a.dur_min != null && dur < a.dur_min - 0.5) issues.push(i18n.t('max.reasonUnderDur', { sec: a.dur_min }));
    }
  }
  if (ext === 'zip' && a.needs_clicktag) issues.push(...(await zipIssues(file)));
  return issues;
}

// Inspect an HTML5 ZIP for the facts each platform validates differently:
// file count, index.html size, and which click mechanism it uses (IAB var clickTag
// vs Yandex's yandexHTML5BannerApi). Cheap, cached implicitly by the caller.
async function analyzeZip(file: File): Promise<{ files: number; indexKb: number; hasClickTag: boolean; hasYandexApi: boolean; hardcoded: boolean }> {
  const out = { files: 0, indexKb: 0, hasClickTag: false, hasYandexApi: false, hardcoded: false };
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(file);
    const names = Object.keys(zip.files).filter(f => !zip.files[f].dir && !f.startsWith('__MACOSX'));
    out.files = names.length;
    const idx = names.find(f => /(^|\/)index\.html?$/i.test(f));
    for (const h of names.filter(f => /\.html?$/i.test(f))) {
      const c = await zip.files[h].async('string');
      if (/var\s+clickTag\s*=/i.test(c) || /window\.clickTag/i.test(c)) out.hasClickTag = true;
      if (/yandexHTML5BannerApi|getClickURLNum/i.test(c)) out.hasYandexApi = true;
      if (/onclick\s*=\s*["']window\.open\s*\(\s*["']https?:\/\//i.test(c)) out.hardcoded = true;
    }
    if (idx) out.indexKb = (await zip.files[idx].async('uint8array')).length / 1024;
  } catch { /* leave zeros */ }
  return out;
}

// Does ONE uploaded file satisfy ONE format's asset spec — using that format's OWN
// per-type limits (image vs HTML5 caps differ, GDN vs Yandex differ). This is the
// core of per-platform validation: a file can pass GDN and fail Yandex.
function fileFitsAsset(item: any, a: any): { ok: boolean; reason?: string } {
  // jpeg == jpg (WhatsApp/exports use .jpeg) — normalise so the file_types check
  // doesn't reject a valid JPEG that classify already accepted into a bucket.
  const ext = (item.ext || '').toLowerCase().replace(/^jpeg$/, 'jpg');
  const accepts: string[] = a.accepts || [];
  const cap = (mb: number | null | undefined) => mb != null && item.file && item.file.size / 1e6 > mb;

  if (item.kind === 'video') {
    if (!accepts.includes('VIDEO')) return { ok: false, reason: i18n.t('max.reasonNotVideoSlot') };
    if (a.file_types?.length && !a.file_types.includes(ext)) return { ok: false, reason: i18n.t('max.reasonExtNotAccepted', { ext }) };
    if (cap(a.max_size_mb)) return { ok: false, reason: i18n.t('max.reasonOverSize', { size: sizeLabel(a.max_size_mb) }) };
    if (item.durSec) {
      if (a.dur_max != null && item.durSec > a.dur_max + 0.5) return { ok: false, reason: i18n.t('max.reasonOverDur', { sec: a.dur_max }) };
      if (a.dur_min != null && item.durSec < a.dur_min - 0.5) return { ok: false, reason: i18n.t('max.reasonUnderDur', { sec: a.dur_min }) };
    }
    if (a.ratios?.length && item.w && item.h) {
      if (!ratioMatches(item.w / item.h, a.ratios)) return { ok: false, reason: i18n.t('max.reasonRatioOff', { ratio: (item.w / item.h).toFixed(2) }) };
    }
    return { ok: true };
  }

  if (ext === 'zip' || item.kind === 'zip') {
    if (!accepts.includes('HTML5')) return { ok: false, reason: i18n.t('max.reasonNoHtml5Here') };
    const html5Cap = a.html5_max_mb ?? a.max_size_mb;
    if (cap(html5Cap)) return { ok: false, reason: i18n.t('max.reasonZipOver', { size: sizeLabel(html5Cap) }) };
    const z = item.zipInfo;
    if (z) {
      if (a.html5_max_files != null && z.files > a.html5_max_files) return { ok: false, reason: i18n.t('max.reasonTooManyFiles', { files: z.files, max: a.html5_max_files }) };
      if (a.html5_index_max_kb != null && z.indexKb > a.html5_index_max_kb) return { ok: false, reason: i18n.t('max.reasonIndexTooBig', { kb: a.html5_index_max_kb }) };
      if (a.clicktag === 'yandex' && !z.hasYandexApi) return { ok: false, reason: i18n.t('max.reasonNeedsYandexApi') };
      if (a.clicktag === 'iab' && !z.hasClickTag) return { ok: false, reason: i18n.t('max.reasonNeedsClickTag') };
    }
    if (a.sizes_px?.length && item.html5?.w) {
      if (!a.sizes_px.includes(`${item.html5.w}x${item.html5.h}`)) return { ok: false, reason: i18n.t('max.reasonSizeOff', { w: item.html5.w, h: item.html5.h }) };
    }
    return { ok: true };
  }

  if (item.kind === 'image') {
    if (!accepts.includes('IMAGE')) return { ok: false, reason: i18n.t('max.reasonNoImageHere') };
    if (a.file_types?.length && !a.file_types.includes(ext)) return { ok: false, reason: i18n.t('max.reasonExtNotAccepted', { ext }) };
    const imgCap = a.image_max_mb ?? a.max_size_mb;
    if (cap(imgCap)) return { ok: false, reason: i18n.t('max.reasonOverSize', { size: sizeLabel(imgCap) }) };
    if (item.w && item.h) {
      const ar = item.w / item.h;
      const sizeOk = (a.sizes_px || []).includes(`${item.w}x${item.h}`);
      const ratioOk = ratioMatches(ar, a.ratios);
      // Ratio-band rules (e.g. Yandex): pass if the image fits ANY band's ratio + pixel bounds.
      const rangeOk = (a.ratio_ranges || []).some((rr: any) =>
        ar >= rr.min - 0.02 && ar <= rr.max + 0.02 &&
        item.w >= (rr.min_w ?? 0) && item.h >= (rr.min_h ?? 0) &&
        item.w <= (rr.max_w ?? Infinity) && item.h <= (rr.max_h ?? Infinity));
      if ((a.sizes_px?.length || a.ratios?.length || a.ratio_ranges?.length) && !sizeOk && !ratioOk && !rangeOk) {
        // Lead with the uploaded size; if the ratio is a known banner ratio, name the exact target.
        const exact = a.sizes_px?.length ? nearestExactSize(ar, [a.sizes_px]) : null;
        return { ok: false, reason: exact
          ? i18n.t('max.reasonUseExactly', { w: item.w, h: item.h, size: exact })
          : i18n.t('max.reasonNotSupportedSize', { w: item.w, h: item.h }) };
      }
    }
    return { ok: true };
  }
  return { ok: false, reason: i18n.t('max.reasonUnsupportedType') };
}

// Readable spec for an asset option (shown in the (?) hint).
function assetSpec(a: any): string {
  const parts: string[] = [];
  if (a.sizes_px?.length) parts.push(a.sizes_px.join(', '));
  else if (a.ratios?.length) parts.push(a.ratios.join(' / '));
  else if (a.ratio_ranges?.length) parts.push('3:4–4:3 / 16:9');
  if (a.dur_max != null) parts.push(`${a.dur_min || 0}–${a.dur_max}s`);
  if (a.file_types?.length) parts.push(a.file_types.join('/').toUpperCase());
  if (a.max_size_mb != null) parts.push(a.max_size_mb < 1 ? `≤${Math.round(a.max_size_mb * 1000)}KB` : `≤${a.max_size_mb}MB`);
  return parts.join(' · ');
}

// Route a dropped file into the right asset group by type + dimensions/ratio.
function classifyAsset(file: File, dims: { w: number; h: number }, assets: any[]): { key: string | null; reason?: string } {
  const has = (k: string) => assets.some(a => a.key === k);
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (file.type.startsWith('video/')) return has('video') ? { key: 'video' } : { key: null, reason: i18n.t('max.reasonNoVideoFormat') };
  if (ext === 'zip') return has('banner') ? { key: 'banner' } : { key: null, reason: i18n.t('max.reasonNoHtml5Format') };
  if (file.type.startsWith('image/')) {
    const { w, h } = dims;
    if (!w || !h) return { key: null, reason: i18n.t('max.reasonUnreadableImage') };
    // Exact fixed-size banner → its own IAB category (e.g. Yandex TextImageAd) before ratio buckets.
    const iab = assets.find(a => a.key === 'iab_banner');
    if (iab && (iab.sizes_px || []).includes(`${w}x${h}`)) return { key: 'iab_banner' };
    const banner = assets.find(a => a.key === 'banner');
    if (banner && (banner.sizes_px || []).includes(`${w}x${h}`)) return { key: 'banner' }; // exact IAB size
    const r = w / h;
    // Route to the nearest EXISTING bucket within the shared tolerance. (Buckets a
    // format doesn't offer aren't created, so a ratio with no matching bucket is
    // rejected honestly — e.g. Meta 4:5/9:16 until placement-customization lands.)
    const cands: [string, number][] = [['image_landscape', 1.91], ['image_square', 1], ['image_portrait', 0.8], ['image_vertical', 0.5625]];
    let best: string | null = null, bestD = RATIO_TOL;
    for (const [k, rv] of cands) { const d = Math.abs(r - rv); if (has(k) && d < bestD) { best = k; bestD = d; } }
    if (best) return { key: best };
    // Rejections lead with the uploaded SIZE (not ratio) so the client sees exactly what they
    // dropped. Display slots need the EXACT pixels (no resizing on our side); if the ratio is
    // right but the size isn't, name the exact size to export to.
    const exact = nearestExactSize(r, [iab?.sizes_px, banner?.sizes_px]);
    if (exact) return { key: null, reason: i18n.t('max.reasonUseExactly', { w, h, size: exact }) };
    return { key: null, reason: i18n.t('max.reasonNotSupportedSize', { w, h }) };
  }
  return { key: null, reason: i18n.t('max.reasonUnsupportedType') };
}

// Does a file fit a specific option? Used so the clicked "+ Add" tile gets priority.
function fitsOption(file: File, dims: { w: number; h: number }, opt: any): boolean {
  if (!opt) return false;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (opt.key === 'video') return file.type.startsWith('video/');
  if (ext === 'zip') return (opt.accepts || []).includes('HTML5');
  if (file.type.startsWith('image/')) {
    if (!(opt.accepts || []).includes('IMAGE')) return false;
    const { w, h } = dims;
    if (!w || !h) return false;
    if ((opt.sizes_px || []).includes(`${w}x${h}`)) return true;
    return ratioMatches(w / h, opt.ratios);
  }
  return false;
}

// Parse a pasted/loaded keyword blob (newline / comma / semicolon separated), deduped.
function parseKw(text: string): string[] {
  const seen = new Set<string>(); const out: string[] = [];
  text.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean).forEach(k => {
    if (!seen.has(k)) { seen.add(k); out.push(k); }
  });
  return out;
}

const PLAT_COLORS: Record<string, string> = {
  sense: '#8E3A9A',      // purple (brand)
  google_ads: '#FF7A45', // warm orange
  meta: '#2E7DF6',       // blue
  yandex: '#F5B32B',     // gold
  linkedin: '#17B890',   // teal-green
  tiktok: '#E84393',     // magenta
};

interface BpAlloc { channel: string; platform: string; share_client: number; min_share?: number; }

/**
 * Interactive budget-split bar. The client total is split across the launch-ready platforms;
 * each boundary between two adjacent segments is a draggable handle that moves budget between
 * those two platforms only (the rest stay put). Each slice is floored at its minimum — Sense at
 * `senseMin` (15%), an external at its API minimum — so a drag can never push a platform below
 * what it needs to launch. Dragging sets `onChange({channel: fraction})`; the launch applies the
 * SAME АК + reseller-markup gross-down to those fractions as the auto split, so money stays right.
 */
const BudgetSplitBar: React.FC<{
  alloc: BpAlloc[]; total: number; currency: string; senseMin: number;
  split: Record<string, number> | null;
  onChange: (s: Record<string, number>) => void;
}> = ({ alloc, total, currency, senseMin, split, onChange }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [dragK, setDragK] = useState<number | null>(null);

  // Fixed canonical order (NOT share-desc) so segments never reorder mid-drag when shares change.
  const CANON = ['sense', 'google_ads', 'meta', 'yandex', 'tiktok', 'linkedin'];
  const allocSorted = [...alloc].sort(
    (a, b) => (CANON.indexOf(a.channel) + 1 || 99) - (CANON.indexOf(b.channel) + 1 || 99));
  const order = allocSorted.map(a => a.channel);
  const minOf = (ch: string) =>
    ch === 'sense' ? Math.max(senseMin, 0) : (alloc.find(a => a.channel === ch)?.min_share || 0);
  const fracs: number[] = (() => {
    let raw: number[];
    if (split && order.every(c => split[c] != null)) raw = order.map(c => split[c]);
    else raw = allocSorted.map(a => (total ? a.share_client / total : 0));
    const s = raw.reduce((a, b) => a + b, 0) || 1;
    return raw.map(v => v / s);
  })();

  const commit = (next: number[]) => {
    const out: Record<string, number> = {};
    order.forEach((c, i) => { out[c] = next[i]; });
    onChange(out);
  };

  const onDown = (k: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDragK(k);
  };

  useEffect(() => {
    if (dragK === null) return;
    const k = dragK;
    const move = (e: PointerEvent) => {
      const el = barRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const mouseFrac = Math.min(1, Math.max(0, (e.clientX - r.left) / (r.width || 1)));
      const prefix = fracs.slice(0, k).reduce((a, b) => a + b, 0);   // left edge of segment k
      const pairSpan = fracs[k] + fracs[k + 1];
      const lo = prefix + minOf(order[k]);
      const hi = prefix + pairSpan - minOf(order[k + 1]);
      const boundary = Math.min(hi, Math.max(lo, mouseFrac));
      const next = [...fracs];
      next[k] = boundary - prefix;
      next[k + 1] = pairSpan - next[k];
      commit(next);
    };
    const up = () => setDragK(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [dragK, fracs, order]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className={`bp-stack bp-stack-drag ${dragK !== null ? 'bp-dragging' : ''}`} ref={barRef}>
        {order.map((ch, i) => {
          const left = fracs.slice(0, i).reduce((a, b) => a + b, 0);
          return (
            <React.Fragment key={ch}>
              <div className="bp-seg" style={{ width: `${fracs[i] * 100}%`, background: PLAT_COLORS[ch] || '#bbb' }}
                   title={`${allocSorted[i].platform} · ${Math.round(fracs[i] * 100)}%`} />
              {i < order.length - 1 && (
                <div className="bp-handle" style={{ left: `${(left + fracs[i]) * 100}%` }}
                     onPointerDown={onDown(i)} role="separator" aria-label="drag to redistribute">
                  <span className="bp-handle-grip" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="bp-legend">
        {order.map((ch, i) => (
          <div key={ch} className="bp-leg">
            <span className="bp-dot" style={{ background: PLAT_COLORS[ch] || '#bbb' }} />
            <span className="bp-leg-name">{allocSorted[i].platform}</span>
            <span className="bp-leg-pct">{Math.round(fracs[i] * 100)}%</span>
            <span className="bp-leg-amt">{Math.round(fracs[i] * total).toLocaleString()} {currency}</span>
          </div>
        ))}
      </div>
    </>
  );
};

const SenseMaxPage: React.FC = () => {
  // The constructor is sold under the partner's own name on a white-label, so
  // the page title follows the button that opens it: "<brand> omni" there,
  // "Sense Max" on the main instance. Read per render, not at module load —
  // branding resolves from the backend at startup.
  const { t } = useTranslation();
  const branding = getBranding();
  const whiteLabel = branding.edition !== 'sense';
  const brandName = branding.brand_name.toLowerCase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // "Manage" mode: opened from a launched campaign's dashboard to change creatives/texts.
  // The action becomes Apply-changes (replace on platforms), gated on every LAUNCHED format
  // keeping its required materials (you can't drop a creative and leave that format empty).
  const isManage = searchParams.get('manage') === '1';
  const [launchedFormats, setLaunchedFormats] = useState<string[]>([]);
  const { selectedAdvertiserId, selectedAdvertiser, isAllClients } = useAdvertiser();
  const { user } = useAuth();

  // Client billing currency + EUR rate (rate = units of currency per 1 EUR;
  // EUR amount = local / rate). Budget/KPI show the client currency; admins see a
  // EUR-equivalent hint — same as the normal campaign editor.
  const [currency, setCurrency] = useState('EUR');
  const [currencyRate, setCurrencyRate] = useState(1);

  // Campaign level
  const [campaignName, setCampaignName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [campaignStatus, setCampaignStatus] = useState<'active' | 'paused'>('active');
  const [draftId, setDraftId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [triedSave, setTriedSave] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [confirmLaunch, setConfirmLaunch] = useState(false);   // "Save and Launch" confirmation
  // Cross-platform launch result: per-channel outcomes returned by POST /launch.
  const [launchResult, setLaunchResult] = useState<any>(null);

  // Ad-group level
  const [budget, setBudget] = useState('');
  // Live preview of how the budget splits across ready platforms (+ which get excluded).
  const [budgetPreview, setBudgetPreview] = useState<any>(null);
  // Manual per-channel split (channel → fraction, Σ=1) the user dragged. null = auto split.
  const [budgetSplit, setBudgetSplit] = useState<Record<string, number> | null>(null);
  const [bidStrategy, setBidStrategy] = useState('CPC');
  const [kpiAmount, setKpiAmount] = useState('');
  // Conversion (Floodlight) activities for this client. CPA bidding is only offered when the
  // client has a pixel configured (activities.length > 0), and requires picking one.
  const [convActivities, setConvActivities] = useState<{ id: string; name: string; type?: string }[]>([]);
  const [optimizationGoalIds, setOptimizationGoalIds] = useState<string[]>([]);
  // Per-client platform capabilities (probed server-side, cached). Gates formats that need one
  // — e.g. google_demand_gen needs google_conversions (conversion tracking on the Google account).
  const [caps, setCaps] = useState<Record<string, boolean | null>>({});
  const [freqCount, setFreqCount] = useState('');
  const [freqPeriod, setFreqPeriod] = useState('week');
  const [ages, setAges] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);
  const [parental, setParental] = useState<string[]>([]);
  const [devices, setDevices] = useState<string[]>([]);
  const [placementEnv, setPlacementEnv] = useState<string[]>([]);
  // Blacklist = the client's saved URL lists (ads_channels), picked here and shared with
  // the normal campaign editor. `blacklist` only survives to reopen older drafts that
  // stored typed-in domains before the lists existed.
  const [blacklist, setBlacklist] = useState('');
  const [blLists, setBlLists] = useState<{ channel_id: string; name: string; sites_count: number }[]>([]);
  const [blSelected, setBlSelected] = useState<string[]>([]);
  const [blNewOpen, setBlNewOpen] = useState(false);
  const [blNewName, setBlNewName] = useState('');
  const [blNewSites, setBlNewSites] = useState('');
  const [blNewErr, setBlNewErr] = useState<string | null>(null);
  const [blSaving, setBlSaving] = useState(false);
  const [advanced, setAdvanced] = useState<any[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [landingUrl, setLandingUrl] = useState('');
  const [urlInfo, setUrlInfo] = useState<any>(null);
  const [urlChecking, setUrlChecking] = useState(false);
  // Geo (regions): included / excluded + debounced autocomplete search
  const [geoIncluded, setGeoIncluded] = useState<Geo[]>([]);
  const [geoExcluded, setGeoExcluded] = useState<Geo[]>([]);
  const [geoIn, setGeoIn] = useState('');
  const [geoRes, setGeoRes] = useState<Geo[]>([]);
  const [geoLoad, setGeoLoad] = useState(false);
  const [geoExIn, setGeoExIn] = useState('');
  const [geoExRes, setGeoExRes] = useState<Geo[]>([]);
  const [geoExLoad, setGeoExLoad] = useState(false);
  const [dayTime, setDayTime] = useState<Set<string>>(() => new Set(ALL_CELLS));
  const [dragging, setDragging] = useState(false);
  const [showDayTime, setShowDayTime] = useState(false);
  const [showCreatives, setShowCreatives] = useState(true);
  // Creatives — unique options deduped across all formats (enter once, applies to all)
  const [creativeOpts, setCreativeOpts] = useState<{ assets: any[]; texts: any[] }>({ assets: [], texts: [] });
  const [catalogFailed, setCatalogFailed] = useState(false);
  const [catalogReload, setCatalogReload] = useState(0);
  const [catalog, setCatalog] = useState<any>(null);
  const [showPreviews, setShowPreviews] = useState(true);
  const [showMoreTexts, setShowMoreTexts] = useState(false);
  const [selectedFmt, setSelectedFmt] = useState<string | null>(null);
  // Formats switched OFF in the gallery before publishing (excluded from launch +
  // budget split). Default empty = launch every ready format. The 1+1 rule keeps at
  // least one Sense tool and one external switched on.
  const [disabledFormats, setDisabledFormats] = useState<Set<string>>(new Set());
  // Meta lead form (the 'meta_lead' format attaches an instant form instead of a landing URL).
  const [leadFormId, setLeadFormId] = useState<string>('');
  const [leadForms, setLeadForms] = useState<{ id: string; name: string; status?: string }[]>([]);
  const [leadFormsBusy, setLeadFormsBusy] = useState(false);
  // TikTok instant form (the 'tiktok_lead' format attaches an instant form; mirror of meta_lead).
  const [tiktokLeadFormId, setTiktokLeadFormId] = useState<string>('');
  const [tiktokLeadForms, setTiktokLeadForms] = useState<{ id: string; name: string; status?: string }[]>([]);
  const [tiktokLeadFormsBusy, setTiktokLeadFormsBusy] = useState(false);
  const [cTexts, setCTexts] = useState<Record<string, string>>({});
  const [cMulti, setCMulti] = useState<Record<string, string[]>>({});
  const [genBusy, setGenBusy] = useState<Record<string, boolean>>({});
  const [cChosen, setCChosen] = useState<Record<string, number[]>>({});
  const [cUploaded, setCUploaded] = useState<Record<string, { id: number; file: File; url: string | null; name: string; ext: string; issues: string[]; checking: boolean; stored?: string; html5?: { src: string; w: number; h: number }; kind?: string; w?: number; h?: number; durSec?: number; zipInfo?: { files: number; indexKb: number; hasClickTag: boolean; hasYandexApi: boolean; hardcoded: boolean } }[]>>({});
  const [library, setLibrary] = useState<any[]>([]);
  const [picker, setPicker] = useState<{ key: string; accepts: string[]; label: string } | null>(null);
  const [bulkSummary, setBulkSummary] = useState<{ distributed: Record<string, number>; rejected: { name: string; reason: string }[]; duplicates?: number } | null>(null);
  const dragMode = useRef<'select' | 'deselect'>('select');
  const idRef = useRef(0);
  const cDropInput = useRef<HTMLInputElement>(null);
  // Manage-mode baseline (the launched content) — used to diff what actually changed so
  // replace only touches the affected formats.
  const baselineRef = useRef<{ creatives: Record<string, string[]>; texts: Record<string, any>; multi: Record<string, any> } | null>(null);
  const [cDragging, setCDragging] = useState(false);
  const ytTimer = useRef<any>(null);
  const ytCache = useRef<Record<string, any>>({});
  const [ytText, setYtText] = useState('');
  const [kwText, setKwText] = useState('');
  const [ytInfos, setYtInfos] = useState<Record<string, any>>({});

  useEffect(() => {
    const up = () => setDragging(false);
    document.addEventListener('mouseup', up);
    return () => document.removeEventListener('mouseup', up);
  }, []);

  // Unique creative options (deduped across formats)
  useEffect(() => {
    // The client decides both the gallery (Ukraine offices drop Yandex) and the
    // currency, and a call made before one is picked answers in EUR. That answer
    // used to arrive after the client's own and overwrite it, so a KZT client
    // could sit there budgeting in euro. Wait for the client, and ignore any
    // reply that a newer request has already superseded.
    if (!selectedAdvertiserId || isAllClients) return;
    let current = true;
    api.get('/api/sense-max/catalog', { params: { advertiser_id: selectedAdvertiserId } })
      .then(r => {
        if (!current) return;
        setCatalog(r.data);
        setCurrency(r.data.currency || 'EUR');
        setCurrencyRate(r.data.currency_rate || 1);
        const co = r.data.creative_options || { assets: [], texts: [] };
        // Logo is no longer user-uploaded — drop it from the asset slots so it has
        // no upload UI / classify logic. A fixed logo file is used everywhere instead.
        setCreativeOpts({ ...co, assets: (co.assets || []).filter((a: any) => a.key !== 'logo') });
        setCatalogFailed(false);
      })
      // A silent failure here left the asset options empty, and an empty option
      // list makes EVERY dropped file "not a supported size" — a message that
      // blames the creative for a catalogue that never loaded. Remember the
      // failure so the drop zone can say what actually happened.
      .catch(() => { if (current) setCatalogFailed(true); });
    return () => { current = false; };
  }, [selectedAdvertiserId, isAllClients, catalogReload]);

  // Meta instant/lead forms for the 'meta_lead' format — loaded whenever that format is
  // available for this client (Meta connected), since all formats are configured at once.
  const metaLeadAvailable = (catalog?.formats || []).some((f: any) => f.id === 'meta_lead');
  useEffect(() => {
    if (!metaLeadAvailable || !selectedAdvertiserId || isAllClients) { setLeadForms([]); return; }
    let alive = true;
    setLeadFormsBusy(true);
    api.get('/api/oauth/meta/forms', { params: { advertiser_id: selectedAdvertiserId } })
      .then(r => { if (alive) setLeadForms(r.data?.forms || []); })
      .catch(() => { if (alive) setLeadForms([]); })
      .finally(() => { if (alive) setLeadFormsBusy(false); });
    return () => { alive = false; };
  }, [metaLeadAvailable, selectedAdvertiserId, isAllClients]);

  // TikTok instant forms for the 'tiktok_lead' format — same pattern as Meta above.
  const tiktokLeadAvailable = (catalog?.formats || []).some((f: any) => f.id === 'tiktok_lead');
  useEffect(() => {
    if (!tiktokLeadAvailable || !selectedAdvertiserId || isAllClients) { setTiktokLeadForms([]); return; }
    let alive = true;
    setTiktokLeadFormsBusy(true);
    api.get('/api/oauth/tiktok/forms', { params: { advertiser_id: selectedAdvertiserId } })
      .then(r => { if (alive) setTiktokLeadForms(r.data?.forms || []); })
      .catch(() => { if (alive) setTiktokLeadForms([]); })
      .finally(() => { if (alive) setTiktokLeadFormsBusy(false); });
    return () => { alive = false; };
  }, [tiktokLeadAvailable, selectedAdvertiserId, isAllClients]);

  // The client's saved URL lists, for the blacklist picker.
  const loadBlLists = useCallback(async () => {
    if (!selectedAdvertiserId || isAllClients) { setBlLists([]); return; }
    try {
      // Same endpoint and shape the campaign editor reads, so both screens show
      // the client the identical set of lists.
      const r = await api.get(`/api/reports/channels/${selectedAdvertiserId}`);
      setBlLists(r.data?.black_list || []);
    } catch { setBlLists([]); }
  }, [selectedAdvertiserId, isAllClients]);
  useEffect(() => { loadBlLists(); }, [loadBlLists]);

  // Create a list right here and use it — same endpoint the campaign editor calls, so
  // it lands in the client's URL Lists like any other.
  const saveNewBlacklist = async () => {
    const sites = blNewSites.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (!blNewName.trim() || !sites.length || !selectedAdvertiserId) return;
    setBlSaving(true); setBlNewErr(null);
    try {
      const r = await api.post('/api/channels/create', {
        advertiser_id: selectedAdvertiserId, display_name: blNewName.trim(), sites,
      });
      // A long list is split into several channels server-side — take every id, or
      // only the first part of the blacklist would be excluded.
      const ids: string[] = (r.data?.channel_ids && r.data.channel_ids.length)
        ? r.data.channel_ids.map(String)
        : (r.data?.id ? [String(r.data.id)] : []);
      await loadBlLists();
      if (ids.length) setBlSelected(prev => [...prev, ...ids.filter(i => !prev.includes(i))]);
      setBlNewOpen(false); setBlNewName(''); setBlNewSites('');
    } catch (e: any) {
      setBlNewErr(e?.response?.data?.detail || t('max.errSaveList'));
    } finally {
      setBlSaving(false);
    }
  };

  // Existing creatives to Choose from
  useEffect(() => {
    if (!selectedAdvertiserId || isAllClients) { setLibrary([]); return; }
    api.get(`/api/creatives?advertiser_id=${selectedAdvertiserId}`)
      .then(r => { const d = r.data; setLibrary(Array.isArray(d) ? d : (d.creatives || d.items || [])); })
      .catch(() => setLibrary([]));
  }, [selectedAdvertiserId, isAllClients]);

  // Conversion activities (pixels) for this client — gate the CPA strategy on having ≥1.
  useEffect(() => {
    if (!selectedAdvertiserId || isAllClients) { setConvActivities([]); return; }
    api.get(`/api/conversions/${selectedAdvertiserId}/activities`)
      .then(r => setConvActivities(Array.isArray(r.data) ? r.data : (r.data?.activities || [])))
      .catch(() => setConvActivities([]));
  }, [selectedAdvertiserId, isAllClients]);

  useEffect(() => {
    if (!selectedAdvertiserId || isAllClients) { setCaps({}); return; }
    api.get(`/api/sense-max/capabilities`, { params: { advertiser_id: selectedAdvertiserId } })
      .then(r => setCaps(r.data?.capabilities || {}))
      .catch(() => setCaps({}));
  }, [selectedAdvertiserId, isAllClients]);

  // Upload an HTML5 ZIP → server unzips & serves it; returns the iframe src + ad size.
  const uploadHtml5 = async (file: File): Promise<{ src: string; w: number; h: number } | null> => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/api/sense-max/html5-preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data;
      if (!d || !d.ok) return null;
      const base = (api.defaults.baseURL || '').replace(/\/$/, '');
      return { src: `${base}/api/sense-max/html5-preview/${d.id}/${d.entry}`, w: d.w, h: d.h };
    } catch { return null; }
  };

  // Compute the per-file metadata that per-format validation reads (image dims,
  // video duration, HTML5 zip facts). No merged-spec verdict here — a file is judged
  // separately by EACH format's own spec (fileFitsAsset), so we never over-reject.
  const captureMeta = (k: string, it: any) => {
    (async () => {
      const patch: any = { checking: false };
      if (it.kind === 'image') { const d = await imgDims(it.file); patch.w = d.w; patch.h = d.h; }
      else if (it.kind === 'video') { const m = await vidMeta(it.file); patch.durSec = m.dur; }
      else if (it.kind === 'zip') { patch.zipInfo = await analyzeZip(it.file); }
      setCUploaded(p => ({ ...p, [k]: (p[k] || []).map(x => x.id === it.id ? { ...x, ...patch } : x) }));
    })();
  };
  const kindOf = (f: File) => {
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    return f.type.startsWith('video/') ? 'video' : (ext === 'zip' ? 'zip' : (f.type.startsWith('image/') ? 'image' : 'other'));
  };

  const addItems = (k: string, fileArr: File[], _asset?: any) => {
    if (!fileArr.length) return;
    const items = fileArr.map(f => ({
      id: ++idRef.current, file: f, name: f.name, ext: (f.name.split('.').pop() || '').toUpperCase(),
      url: f.type.startsWith('image/') ? URL.createObjectURL(f) : null, issues: [] as string[],
      checking: true, kind: kindOf(f),
    }));
    setCUploaded(p => ({ ...p, [k]: [...(p[k] || []), ...items] }));
    items.forEach(it => captureMeta(k, it));
    // HTML5 ZIP → server-side unzip + serve, then point the mockup iframe at it
    items.forEach(it => { if (it.ext === 'ZIP') uploadHtml5(it.file).then(h => {
      if (h) setCUploaded(p => ({ ...p, [k]: (p[k] || []).map(x => x.id === it.id ? { ...x, html5: h } : x) }));
    }); });
  };
  // Rebuild uploaded items from a reopened draft's persisted refs. Each File is
  // reconstructed (fetched from its stored URL) so display, the HTML5 preview, and a
  // later launch all behave exactly like a fresh upload. `stored` is carried so a
  // re-save doesn't duplicate the file on the server.
  const restoreItems = (k: string, built: { file: File; ref: any }[]) => {
    if (!built.length) return;
    const items = built.map(({ file, ref }) => ({
      id: ++idRef.current, file, name: ref.name, ext: ref.ext,
      // `file` was already fetched from the stored URL — preview from the blob directly
      // (like a fresh upload) so it never depends on the ref.url being absolute/relative.
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      issues: [] as string[], checking: false, stored: ref.stored as string, kind: kindOf(file),
    }));
    setCUploaded(p => ({ ...p, [k]: [...(p[k] || []), ...items] }));
    items.forEach(it => captureMeta(k, it));
    items.forEach(it => { if (it.ext === 'ZIP') uploadHtml5(it.file).then(h => {
      if (h) setCUploaded(p => ({ ...p, [k]: (p[k] || []).map(x => x.id === it.id ? { ...x, html5: h } : x) }));
    }); });
  };

  // Any "+ Add" → classify each selected file and distribute into the matching group.
  const addBulk = async (files: FileList | null, preferKey?: string) => {
    if (!files || !files.length) return;
    // No formats loaded → nothing can match, and saying "not a supported size"
    // about every file sends people hunting for a problem in their creatives.
    if (!creativeOpts.assets.length) {
      setBulkSummary({
        distributed: {}, duplicates: 0,
        rejected: Array.from(files).map(f => ({
          name: f.name,
          reason: t(catalogFailed ? 'max.reasonCatalogFailed' : 'max.reasonCatalogLoading'),
        })),
      });
      setCatalogReload(n => n + 1);
      return;
    }
    const optByKey: Record<string, any> = {};
    creativeOpts.assets.forEach(a => { optByKey[a.key] = a; });
    const prefOpt = preferKey ? optByKey[preferKey] : null;
    // dedupe PER GROUP (same file may live in Square AND Logo, but not twice in one group)
    const seenByKey: Record<string, Set<string>> = {};
    Object.entries(cUploaded).forEach(([k, arr]) => { seenByKey[k] = new Set((arr || []).map(it => `${it.name}:${it.file.size}`)); });
    const byKey: Record<string, File[]> = {};
    const rejected: { name: string; reason: string }[] = [];
    let duplicates = 0;
    for (const f of Array.from(files)) {
      let dims = { w: 0, h: 0 };
      if (f.type.startsWith('image/')) dims = await imgDims(f);
      let target: string | null = null;
      if (prefOpt && fitsOption(f, dims, prefOpt)) target = preferKey!;
      else { const res = classifyAsset(f, dims, creativeOpts.assets); target = res.key; if (!target) { rejected.push({ name: f.name, reason: res.reason || t('max.reasonNoMatch') }); continue; } }
      const set = seenByKey[target] || (seenByKey[target] = new Set());
      const sig = `${f.name}:${f.size}`;
      if (set.has(sig)) { duplicates++; continue; }
      set.add(sig);
      (byKey[target] = byKey[target] || []).push(f);
    }
    Object.keys(byKey).forEach(k => addItems(k, byKey[k], optByKey[k]));
    setBulkSummary({ distributed: Object.fromEntries(Object.keys(byKey).map(k => [k, byKey[k].length])), rejected, duplicates });
  };
  const removeCFile = (k: string, id: number) => setCUploaded(p => {
    const arr = p[k] || []; const it = arr.find(x => x.id === id);
    if (it?.url) URL.revokeObjectURL(it.url);
    return { ...p, [k]: arr.filter(x => x.id !== id) };
  });

  // Generate text for a field from the landing page (Anthropic), in the page's language.
  const genText = async (fld: any) => {
    if (!landingUrl.trim() || genBusy[fld.key]) return;
    setGenBusy(p => ({ ...p, [fld.key]: true }));
    try {
      const count = fld.key === 'keywords' ? 40 : (fld.multiline ? (fld.count_max || 10) : 1);
      const r = await api.get('/api/sense-max/generate-text', {
        // The catalog's own English label — it names the field to the generator,
        // which writes in the page's language. Never the translated label.
        params: { url: landingUrl, field: fld.label, max_len: fld.max_len || 0, count },
      });
      const sug: string[] = r.data?.suggestions || [];
      if (sug.length) {
        if (fld.key === 'keywords') {
          setKwText(prev => { const m = parseKw(prev); for (const s of sug) if (!m.includes(s)) m.push(s); return m.join('\n'); });
        } else if (fld.multiline) setCMulti(p => {
          const cur = p[fld.key] || [];
          const merged = [...cur];
          for (const s of sug) if (!merged.includes(s)) merged.push(s);
          return { ...p, [fld.key]: merged.slice(0, fld.count_max || 99) };
        });
        else setCTexts(p => ({ ...p, [fld.key]: sug[0] }));
      }
    } catch { /* ignore */ }
    setGenBusy(p => ({ ...p, [fld.key]: false }));
  };

  // Keywords: bulk-load a big list from a .txt/.csv file (merged & deduped).
  const uploadKeywords = async (files: FileList | null) => {
    const f = files && files[0];
    if (!f) return;
    const text = await f.text();
    setKwText(prev => { const m = parseKw(prev); for (const k of parseKw(text)) if (!m.includes(k)) m.push(k); return m.join('\n'); });
  };

  // Multi-value text fields: fill one → a new empty input appears; clearing removes it.
  const setMulti = (key: string, i: number, val: string) => {
    setCMulti(p => {
      const cur = p[key] || [];
      let next: string[];
      if (i >= cur.length) { if (!val) return p; next = [...cur, val]; }
      else { next = cur.map((x, j) => j === i ? val : x); if (!val) next = next.filter((_, j) => j !== i); }
      return { ...p, [key]: next };
    });
  };

  // YouTube videos — one URL per line in a single field; each line auto-parsed
  // and debounce-checked (title/thumbnail). Removing a line removes its preview.
  const onYtChange = (text: string) => {
    setYtText(text);
    clearTimeout(ytTimer.current);
    ytTimer.current = setTimeout(() => {
      const urls = Array.from(new Set(text.split('\n').map(s => s.trim()).filter(Boolean)));
      urls.forEach(async u => {
        if (ytCache.current[u] && !ytCache.current[u].checking) return; // already resolved
        ytCache.current[u] = { checking: true };
        setYtInfos(p => ({ ...p, [u]: { checking: true } }));
        try {
          const r = await api.get('/api/sense-max/youtube-check', { params: { url: u } });
          ytCache.current[u] = r.data; setYtInfos(p => ({ ...p, [u]: r.data }));
        } catch {
          const e = { ok: false, error: 'check failed' };
          ytCache.current[u] = e; setYtInfos(p => ({ ...p, [u]: e }));
        }
      });
    }, 600);
  };
  const toggleCChosen = (k: string, id: number) => setCChosen(p => {
    const cur = p[k] || [];
    return { ...p, [k]: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
  });
  const clearCAsset = (k: string) => { setCChosen(p => ({ ...p, [k]: [] })); setCUploaded(p => ({ ...p, [k]: [] })); };

  // Landing URL preview: server-side reachability + tiny page info (debounced)
  useEffect(() => {
    const v = landingUrl.trim();
    if (v.length < 4 || !/\.[a-z]{2,}/i.test(v)) { setUrlInfo(null); setUrlChecking(false); return; }
    setUrlChecking(true);
    // Named `timer`, not `t` — `t` is the translator in this scope.
    const timer = setTimeout(async () => {
      try {
        const r = await api.get('/api/sense-max/url-check', { params: { url: v } });
        setUrlInfo(r.data);
      } catch { setUrlInfo({ ok: false, error: t('max.errUrlCheck') }); }
      finally { setUrlChecking(false); }
    }, 600);
    return () => clearTimeout(timer);
  }, [landingUrl]);

  const advForGeo = !isAllClients && selectedAdvertiserId && selectedAdvertiserId !== '__all__' ? selectedAdvertiserId : null;

  useEffect(() => {
    if (geoIn.length < 2 || !advForGeo) { setGeoRes([]); return; }
    const t = setTimeout(async () => {
      setGeoLoad(true);
      try {
        const r = await api.get('/api/targeting/geo/search', { params: { q: geoIn, advertiser_id: advForGeo, limit: 10 } });
        setGeoRes(r.data.results || []);
      } catch { setGeoRes([]); } finally { setGeoLoad(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [geoIn, advForGeo]);

  useEffect(() => {
    if (geoExIn.length < 2 || !advForGeo) { setGeoExRes([]); return; }
    const t = setTimeout(async () => {
      setGeoExLoad(true);
      try {
        const r = await api.get('/api/targeting/geo/search', { params: { q: geoExIn, advertiser_id: advForGeo, limit: 10 } });
        setGeoExRes(r.data.results || []);
      } catch { setGeoExRes([]); } finally { setGeoExLoad(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [geoExIn, advForGeo]);

  const asCanon = (r: Geo): Geo => ({
    id: r.id, name: r.name, type: r.type,
    country: r.country, level: r.level, canonical_name: r.canonical_name || r.name,
  });
  const selectGeo = (r: Geo) => {
    if (!geoIncluded.some(l => l.name === r.name)) {
      setGeoIncluded([...geoIncluded, asCanon(r)]);
      setGeoExcluded(geoExcluded.filter(l => l.name !== r.name));
    }
    setGeoIn(''); setGeoRes([]);
  };
  const selectGeoEx = (r: Geo) => {
    if (!geoExcluded.some(l => l.name === r.name)) {
      setGeoExcluded([...geoExcluded, asCanon(r)]);
      setGeoIncluded(geoIncluded.filter(l => l.name !== r.name));
    }
    setGeoExIn(''); setGeoExRes([]);
  };

  const geoColumn = (
    excluded: boolean, list: Geo[], onRemove: (id: string) => void,
    input: string, onInput: (v: string) => void, loading: boolean,
    results: Geo[], onPick: (r: Geo) => void,
  ) => (
    <div className="geo-column">
      <label className={`targeting-label ${excluded ? 'targeting-label-excluded' : ''}`}>
        {excluded && (
          <svg className="excluded-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><path d="M4.93 4.93l14.14 14.14" /></svg>
        )}
        {t(excluded ? 'max.geoExcluded' : 'max.geoLocations')}
      </label>
      <div className="geo-email-container">
        {list.length > 0 && (
          <div className="geo-email-list">
            {list.map(loc => {
              const ci = loc.name.indexOf(',');
              const main = ci > 0 ? loc.name.slice(0, ci) : loc.name;
              const rest = ci > 0 ? loc.name.slice(ci) : '';
              return (
                <div key={loc.id} className="geo-email-item">
                  <span className="geo-email-name">{main}{rest && <span className="geo-name-secondary">{rest}</span>}</span>
                  <button className="geo-email-remove" title={t('max.remove')} onClick={() => onRemove(loc.id)}>×</button>
                </div>
              );
            })}
          </div>
        )}
        <div className="geo-email-search">
          <div className="geo-email-search-wrapper">
            <svg className="geo-email-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            <input type="text" className="geo-email-input" placeholder={t('max.searchLocation')} value={input}
              onChange={e => onInput(e.target.value)}
              onBlur={() => setTimeout(() => { onInput(''); }, 200)} />
            {loading && <span className="geo-email-loading">...</span>}
          </div>
          {results.length > 0 && (
            <div className="geo-email-dropdown">
              {results.map(r => (
                <div key={r.id} className="geo-email-dropdown-item" onClick={() => onPick(r)}>
                  <span className="geo-dropdown-name">{r.name}</span>
                  <span className="geo-dropdown-type">{r.type}</span>
                </div>
              ))}
            </div>
          )}
          {input.length >= 2 && !loading && results.length === 0 && (
            <div className="geo-email-dropdown"><div className="geo-email-dropdown-empty">{t('max.noLocations')}</div></div>
          )}
        </div>
      </div>
    </div>
  );

  const kpiLabel = bidStrategy === 'AV_VIEWED' ? 'CPM' : bidStrategy === 'CPC' ? 'CPC' : bidStrategy === 'CPA' ? 'CPA' : 'CPV';

  // Minimum required to save a draft: name + flight dates.
  const requiredMissing = (): string[] => {
    const m: string[] = [];
    if (!campaignName.trim()) m.push(t('max.reqCampaignName'));
    if (!startDate) m.push(t('max.reqStartDate'));
    if (!endDate) m.push(t('max.reqEndDate'));
    // CPA (conversion) bidding needs a Floodlight activity — DV rejects saving/launching
    // without one, so don't let the draft be saved with CPA and no conversion picked.
    if (bidStrategy === 'CPA' && !optimizationGoalIds.length) m.push(t('max.reqConversionCpa'));
    return m;
  };

  // A platform is launch-ready when at least one of its formats has all required
  // materials (fmtReady). Drives the "Sense + ≥1 other tool" launch rule.
  const platformReady = (plId: string): boolean =>
    (((catalog?.by_platform || {})[plId]) || []).some((f: any) => fmtReady(f));

  // A platform has a format that will actually launch = ready AND not switched off.
  const plEnabledReady = (plId: string): boolean =>
    (((catalog?.by_platform || {})[plId]) || []).some((f: any) => fmtReady(f) && !disabledFormats.has(f.id) && !fmtGate(f));
  const enabledReadyCount = (plId: string): number =>
    (((catalog?.by_platform || {})[plId]) || []).filter((f: any) => fmtReady(f) && !disabledFormats.has(f.id) && !fmtGate(f)).length;
  // The 1+1 rule: keep at least one Sense tool AND one external switched on. A format's
  // toggle is "locked on" when turning it off would drop the last of its side.
  const extEnabledCount = (): number =>
    (catalog?.platforms || []).filter((pl: any) => pl.id !== 'sense')
      .reduce((n: number, pl: any) => n + enabledReadyCount(pl.id), 0);
  const toggleLocked = (plId: string, fid: string): boolean => {
    if (disabledFormats.has(fid)) return false;           // off → can always turn back on
    return plId === 'sense' ? enabledReadyCount('sense') <= 1 : extEnabledCount() <= 1;
  };
  const toggleFormat = (plId: string, fid: string) => {
    if (!disabledFormats.has(fid) && toggleLocked(plId, fid)) return;   // keep ≥1 Sense + ≥1 external
    setDisabledFormats(prev => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid); else next.add(fid);
      return next;
    });
  };

  // Minimum to LAUNCH (beyond the draft basics): budget, KPI target, geo targeting,
  // and Sense + at least one other tool with a ready format.
  const launchMissing = (): string[] => {
    const m = requiredMissing();
    if (!budget || Number(budget) <= 0) m.push(t('max.reqBudget'));
    if (!kpiAmount || Number(kpiAmount) <= 0) m.push(t('max.reqTargetKpi', { kpi: kpiLabel }));
    if (!geoIncluded.length) m.push(t('max.reqGeo'));
    if (catalog) {
      // Count only formats left switched ON (the gallery toggles) — a Max campaign
      // needs the Sense tool + ≥1 EXTERNAL that actually launches: ready, switched on,
      // and funded by the split (not budget-excluded).
      if (!plEnabledReady('sense')) m.push(t('max.reqSenseFormat'));
      const others = (catalog.platforms || []).filter((pl: any) => pl.id !== 'sense');
      const excludedCh = new Set((budgetPreview?.excluded || []).map((e: any) => e.channel));
      if (!others.some((pl: any) => plEnabledReady(pl.id) && !excludedCh.has(pl.id)))
        m.push(t('max.reqOtherTool'));
    }
    return m;
  };

  // Save the constructor state as a flat draft (no groups). Uploaded creative files
  // are persisted to the server (bytes -> stable URL) and referenced in the payload,
  // so a reopened draft restores its materials. Files already stored are reused.
  const handleSaveDraft = async () => {
    if (saving) return;
    if (requiredMissing().length) { setTriedSave(true); return; }  // highlight empty required fields
    if (!selectedAdvertiserId) return;
    setSaving(true);
    try {
      // Persist uploaded creative files; collect refs by asset key for the payload.
      const creatives: Record<string, { stored: string; url: string; name: string; ext: string }[]> = {};
      const nextUploaded = { ...cUploaded };
      for (const [k, arr] of Object.entries(cUploaded)) {
        const refs: { stored: string; url: string; name: string; ext: string }[] = [];
        const newArr = [...arr];
        for (let i = 0; i < newArr.length; i++) {
          const it = newArr[i];
          // ALWAYS persist the canonical stored-file URL — never it.url, which may be a
          // transient blob:/object URL (from the reopen preview) or carry a baked-in host.
          // The `stored` filename is the single source of truth for a saved creative.
          if (it.stored) { refs.push({ stored: it.stored, url: `/uploads/creatives/sense_max/${it.stored}`, name: it.name, ext: it.ext }); continue; }
          const fd = new FormData(); fd.append('file', it.file);
          const up = await api.post('/api/sense-max/drafts/creative', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          refs.push({ stored: up.data.stored, url: `/uploads/creatives/sense_max/${up.data.stored}`, name: it.name, ext: it.ext });
          newArr[i] = { ...it, stored: up.data.stored };
        }
        if (refs.length) creatives[k] = refs;
        nextUploaded[k] = newArr;
      }
      setCUploaded(nextUploaded);

      const payload = {
        start_date: startDate,
        end_date: endDate,
        status: campaignStatus,
        budget,
        budget_split: budgetSplit,   // manual per-channel split (null = auto)
        bid_strategy: bidStrategy,
        kpi_amount: kpiAmount,
        optimization_goal_ids: bidStrategy === 'CPA' ? optimizationGoalIds : [],
        freq: { count: freqCount, period: freqPeriod },
        landing_url: landingUrl,
        targeting: {
          ages, genders, parental, devices, placement_env: placementEnv,
          blacklist_channel_ids: blSelected,
          // Older drafts kept typed-in domains; carried so reopening one doesn't lose them.
          blacklist_domains: blacklist.split(/[\s,;]+/).map(d => d.trim()).filter(Boolean),
          advanced,
          geo_included: geoIncluded, geo_excluded: geoExcluded,
          day_time: Array.from(dayTime),  // Set → array (JSON can't serialize a Set)
        },
        texts: cTexts,
        texts_multi: cMulti,
        keywords: kwText,
        youtube: ytText,
        selected_format: selectedFmt,
        disabled_formats: Array.from(disabledFormats),   // formats switched off in the gallery
        lead_form_id: leadFormId || null,   // Meta lead form (meta_lead format)
        tiktok_lead_form_id: tiktokLeadFormId || null,   // TikTok instant form (tiktok_lead format)
        creatives,                 // uploaded files (persisted refs by asset key)
        chosen: cChosen,           // creatives picked from the existing library (ids)
      };
      const r = await api.post('/api/sense-max/drafts', {
        id: draftId, advertiser_id: selectedAdvertiserId, name: campaignName.trim(), payload,
      });
      setDraftId(r.data.id);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      return r.data.id as number;   // handleLaunch uses this to launch the saved draft
    } catch (e: any) {
      alert(e?.response?.data?.detail || t('max.errSaveDraft'));
    } finally {
      setSaving(false);
    }
  };

  // Launch: enforce the minimum (budget, KPI, geo, Sense + ≥1 other ready tool). If
  // anything's missing, highlight the empty fields and surface the list — same red-
  // highlight pattern as Save. When all minimums are met, persist the draft; the
  // actual cross-platform push is the next milestone.
  // Manage-mode gate: every LAUNCHED format must still be ready (materials + required texts).
  // Returns the names of launched formats that would be left without their materials.
  const manageMissing = (): string[] => {
    if (!catalog) return [];
    const all = (catalog.platforms || []).flatMap((pl: any) => (catalog.by_platform?.[pl.id]) || []);
    const out: string[] = [];
    for (const fid of launchedFormats) {
      const f = all.find((x: any) => x.id === fid);
      if (f && !fmtReady(f)) out.push(f.name);
    }
    return out;
  };

  // Apply creative/text changes to a LAUNCHED campaign: save the draft, then push the new
  // content to every platform (new ad from the new content; the old ad is paused).
  // Diff the current creatives/texts against the launched baseline → which asset kinds and
  // text keys actually changed. Empty diff = nothing to push.
  const computeChanged = (): { creatives: string[]; texts: string[] } => {
    const b = baselineRef.current || { creatives: {}, texts: {}, multi: {} };
    const idOf = (f: any) => f.stored || `NEW:${f.name}`;
    const curCre: Record<string, string[]> = Object.fromEntries(
      Object.entries(cUploaded).map(([k, arr]) => [k, (arr || []).map(idOf).sort()]));
    const creKeys = Array.from(new Set(Object.keys(curCre).concat(Object.keys(b.creatives))));
    const creatives = creKeys.filter(k =>
      JSON.stringify(curCre[k] || []) !== JSON.stringify(b.creatives[k] || []));
    const txtKeys = Array.from(new Set(Object.keys(cTexts)
      .concat(Object.keys(cMulti), Object.keys(b.texts), Object.keys(b.multi))));
    const texts = txtKeys.filter(k => {
      const cur = cMulti[k] ?? (cTexts[k] ?? '');
      const old = b.multi[k] ?? (b.texts[k] ?? '');
      return JSON.stringify(cur) !== JSON.stringify(old);
    });
    return { creatives, texts };
  };

  const handleApplyChanges = async () => {
    if (launching || saving) return;
    if (manageMissing().length) return;   // button is disabled in this case
    const changed = computeChanged();
    if (!changed.creatives.length && !changed.texts.length) {
      alert(t('max.nothingChanged'));
      return;
    }
    setLaunching(true);
    try {
      const id = await handleSaveDraft();
      if (!id) return;
      await api.post('/api/sense-max/replace', { advertiser_id: selectedAdvertiserId, draft_id: id, changed });
      navigate(-1);   // back to the campaign dashboard
    } catch (e: any) {
      setLaunchResult({ error: e?.response?.data?.detail || t('max.errApplyChanges'), channels: {} });
    } finally {
      setLaunching(false);
    }
  };

  const handleLaunch = async () => {
    if (launching || saving) return;
    if (launchMissing().length) return;   // button is disabled in this case
    setLaunching(true);
    try {
      // Persist first (also uploads creatives) so the server launches the exact saved
      // state; handleSaveDraft returns the (possibly new) draft id.
      const id = await handleSaveDraft();
      if (!id) return;   // save failed / validation surfaced its own message
      // Per-format launch — the server creates one job per ready format PAUSED, then (if the
      // status toggle is Active AND all tools launch) flips the whole campaign live. Partial
      // launches stay paused (all-or-none).
      const r = await api.post('/api/sense-max/launch', {
        draft_id: id, advertiser_id: selectedAdvertiserId, activate: campaignStatus === 'active',
        disabled_format_ids: Array.from(disabledFormats),   // formats switched off in the gallery
      });
      // Straight to the campaign dashboard — it polls per-format launch status live.
      const plats = Array.from(new Set((r.data.jobs || []).map((j: any) => j.channel)));
      navigate(`/campaigns/smdraft-${id}/dashboard`, {
        state: { campaign: {
          id: `smdraft-${id}`, name: campaignName, advertiser: '', status: 'draft',
          sense_max: true, sense_max_draft: true, sense_max_draft_id: id,
          sense_max_platforms: plats, start_date: startDate, end_date: endDate,
          budget: Number(budget) || 0, budget_currency: currency,   // client currency (e.g. KZT)
          advertiser_id: selectedAdvertiserId,
        }, justLaunched: true, excluded: r.data.excluded || [] } });
    } catch (e: any) {
      setLaunchResult({ error: e?.response?.data?.detail || t('max.errLaunch'), channels: {} });
    } finally {
      setLaunching(false);
    }
  };

  // Load a saved draft back into the constructor (opened from the Campaigns page).
  const loadDraft = async (id: number) => {
    try {
      const r = await api.get(`/api/sense-max/drafts/${id}`);
      const d = r.data; const p = d.payload || {}; const t = p.targeting || {};
      setCampaignName(d.name || '');
      setStartDate(p.start_date || ''); setEndDate(p.end_date || '');
      setCampaignStatus(p.status === 'paused' ? 'paused' : 'active');
      setBudget(p.budget ?? ''); setBidStrategy(p.bid_strategy || 'CPC'); setKpiAmount(p.kpi_amount ?? '');
      setBudgetSplit(p.budget_split && typeof p.budget_split === 'object' ? p.budget_split : null);
      setOptimizationGoalIds(Array.isArray(p.optimization_goal_ids) ? p.optimization_goal_ids : []);
      setFreqCount(p.freq?.count ?? ''); setFreqPeriod(p.freq?.period || 'week');
      setLandingUrl(p.landing_url || '');
      setAges(t.ages || []); setGenders(t.genders || []); setParental(t.parental || []);
      setDevices(t.devices || []); setPlacementEnv(t.placement_env || []);
      setBlacklist((t.blacklist_domains || []).join('\n'));
      setBlSelected((t.blacklist_channel_ids || []).map((c: any) => String(c)));
      setAdvanced(t.advanced || []);
      setGeoIncluded(t.geo_included || []); setGeoExcluded(t.geo_excluded || []);
      setDayTime(new Set(t.day_time && t.day_time.length ? t.day_time : ALL_CELLS));
      setCTexts(p.texts || {}); setCMulti(p.texts_multi || {});
      // Snapshot the launched content so manage-mode can diff what the user changes.
      baselineRef.current = {
        creatives: Object.fromEntries(Object.entries<any>(p.creatives || {}).map(([k, arr]) =>
          [k, ((arr as any[]) || []).map((f: any) => f.stored).filter(Boolean).sort()])),
        texts: { ...(p.texts || {}) },
        multi: { ...(p.texts_multi || {}) },
      };
      setKwText(p.keywords || ''); setYtText(p.youtube || '');
      setSelectedFmt(p.selected_format || null);
      setDisabledFormats(new Set(p.disabled_formats || []));
      setLeadFormId(p.lead_form_id || '');
      setTiktokLeadFormId(p.tiktok_lead_form_id || '');
      setCChosen(p.chosen || {});
      setDraftId(d.id);
      setTriedSave(false);

      // Rehydrate uploaded creatives: fetch each stored file, rebuild a real File,
      // and feed it through restoreItems (display / HTML5 preview / launch all work).
      setCUploaded({});
      const cr = p.creatives || {};
      const base = (api.defaults.baseURL || '').replace(/\/$/, '');
      for (const [k, refs] of Object.entries<any>(cr)) {
        const built: { file: File; ref: any }[] = [];
        for (const ref of (refs as any[])) {
          try {
            // ref.url may be absolute (http(s)://…, baked at upload time) or relative
            // (/uploads/…). Only prefix the API base for relative URLs — prefixing an
            // absolute one doubles the origin (https://host https://host/…) and the fetch
            // fails, so the creative silently vanishes on reopen.
            const src = /^https?:\/\//i.test(ref.url) ? ref.url : `${base}${ref.url}`;
            const blob = await fetch(src).then(rsp => rsp.blob());
            built.push({ file: new File([blob], ref.name, { type: blob.type }), ref });
          } catch { /* stored file missing — skip it */ }
        }
        restoreItems(k, built);
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail || t('max.errLoadDraft'));
    }
  };

  // Opened from the Campaigns page with ?draft=ID → restore that draft on mount.
  useEffect(() => {
    const id = searchParams.get('draft');
    if (id && /^\d+$/.test(id)) loadDraft(Number(id));
    // In manage mode also learn which formats are actually LIVE, so Apply can require each
    // of them to still have its materials.
    if (isManage && id && /^\d+$/.test(id)) {
      api.get(`/api/sense-max/launch/jobs?draft_id=${id}`)
        .then(r => setLaunchedFormats((r.data?.jobs || []).map((j: any) => j.format_id).filter(Boolean)))
        .catch(() => { /* leave empty — Apply falls back to the launch readiness gate */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Tag = (active: boolean, label: string, onClick: () => void) => (
    <button type="button" key={label}
      className={`targeting-tag targeting-tag-toggle ${active ? 'active' : ''}`}
      onClick={onClick}>{tv(label)}</button>
  );

  const ytLines = Array.from(new Set(ytText.split('\n').map(s => s.trim()).filter(Boolean)));

  // ── Format-preview data (fills live as materials are added) ──────────────────
  const pvText = (key: string) => (cMulti[key] && cMulti[key][0]) || cTexts[key] || '';
  const pvAll = (key: string): string[] =>
    ((cMulti[key] && cMulti[key].length ? cMulti[key] : (cTexts[key] ? [cTexts[key]] : [])) as string[]).filter(Boolean);
  const pvImg = (...keys: string[]) => {
    for (const k of keys) { const u = (cUploaded[k] || []).find(x => x.url)?.url; if (u) return u; }
    return null;
  };
  const pvDomain = () => {
    try { const raw = landingUrl.startsWith('http') ? landingUrl : 'https://' + landingUrl; return new URL(raw).hostname.replace(/^www\./, ''); }
    catch { return 'your-site.com'; }
  };
  const pvBrand = () => cTexts['business_name'] || (urlInfo && urlInfo.title) || (landingUrl.trim() ? pvDomain() : '');
  const pvYt = () => { for (const l of ytLines) { const i = ytInfos[l]; if (i && i.thumbnail) return i; } return null; };
  const pvBannerNode = () => {
    // Display banner uses ONLY the banner group (HTML5 ZIP or banner image) — never
    // borrows landscape/square images from other groups.
    const z = (cUploaded['banner'] || []).find(x => x.html5);
    if (z && z.html5) {
      const scale = 220 / z.html5.w;
      return (
        <div className="mk-html5-wrap" style={{ width: Math.round(z.html5.w * scale), height: Math.round(z.html5.h * scale) }}>
          <iframe className="mk-html5" title="" sandbox="allow-scripts allow-same-origin" scrolling="no"
            style={{ width: z.html5.w, height: z.html5.h, transform: `scale(${scale})` }} src={z.html5.src} />
        </div>
      );
    }
    const img = (cUploaded['banner'] || []).find(x => x.url)?.url;
    if (img) return <img src={img} alt="" />;
    return null;
  };

  // Is a given canonical creative input filled? (text / asset / keywords / youtube)
  const isInputFilled = (k: string): boolean => {
    if (k === 'keywords') return parseKw(kwText).length > 0;
    if (k === 'youtube_url') return ytLines.length > 0 && !!pvYt();
    if (k === 'logo') return true; // fixed bundled logo — always present
    if (k === 'banner') return (cUploaded['banner'] || []).some((x: any) => x.url || x.html5 || x.file);
    if (k === 'video') return (cUploaded['video'] || []).length > 0;
    if (k === 'iab_banner') // fixed-size banner image slot (Yandex РСЯ / GDN static)
      return (cUploaded['iab_banner'] || []).some((x: any) => x.url || x.file);
    if (k.startsWith('image')) {
      // A ratio-specific slot (square/portrait/landscape/vertical) is satisfied by
      // ITS OWN bucket; a generic 'image' slot by any image bucket.
      const RB = ['image_square', 'image_portrait', 'image_landscape', 'image_vertical'];
      const keys = RB.includes(k) ? [k] : RB;
      return keys.some(ik => (cUploaded[ik] || []).some((x: any) => x.url || x.file));
    }
    return !!pvText(k); // headline, description, body_text, long_headline, business_name, headline2, ad_text, card_headline, cta…
  };

  // ── PER-FORMAT validation (the core fix) ─────────────────────────────────────
  // Every uploaded file is judged against EACH format's OWN spec — so one HTML5 ZIP
  // can pass GDN and fail Yandex, static images feed GDN/Yandex, Meta video counts.
  const ASSET_INPUT_KEYS = new Set(['banner', 'video', 'iab_banner', 'image_square', 'image_landscape', 'image_portrait', 'image_vertical']);
  const poolFiles = (): any[] => Object.values(cUploaded).flat();

  // Catalog labels arrive from the backend in English. Their KEY is canonical and
  // language-independent, so the interface looks the label up by key and falls back
  // to whatever the catalog sent for anything we have no key for. The English label
  // itself is still what travels to the text generator — see genText.
  const inputLabel = (key: string, fallback?: string): string =>
    t(`max.input_${key}`, { defaultValue: fallback || key });

  // Same canonical names the catalog uses for an image slot, so a format's Materials
  // row reads like its "this format uses" row rather than a second wording.
  const IMG_ROLE_KEY: Record<string, string> = {
    landscape: 'image_landscape', square: 'image_square',
    portrait: 'image_portrait', vertical: 'image_vertical',
  };
  const IMG_RATIO_KEY: Record<string, string> = {
    '1.91:1': 'image_landscape', '1:1': 'image_square',
    '4:5': 'image_portrait', '9:16': 'image_vertical',
  };
  const assetLabel = (a: any): string => {
    if (a.type === 'display') return inputLabel('banner');
    if (a.type === 'video') return inputLabel('video');
    if (a.type === 'logo') return inputLabel('logo');
    if (a.type === 'image') {
      const rs = a.ratios || [];
      const canon = IMG_ROLE_KEY[a.role] || (rs.length === 1 ? IMG_RATIO_KEY[rs[0]] : undefined);
      if (canon) return inputLabel(canon);
      return rs.length ? t('max.assetImageRatios', { ratios: rs.join(' / ') }) : t('max.assetImage');
    }
    return inputLabel(a.role || a.type);
  };

  // Uploaded files that PASS this format's spec for asset `a` (+ failing ones with reason).
  const assetFit = (a: any): { pass: any[]; fail: { item: any; reason?: string }[]; pending: any[] } => {
    const pass: any[] = []; const fail: { item: any; reason?: string }[] = []; const pending: any[] = [];
    const wantsVideo = (a.accepts || []).includes('VIDEO');
    for (const it of poolFiles()) {
      const relevant = wantsVideo ? it.kind === 'video' : (it.kind === 'image' || it.kind === 'zip');
      if (!relevant) continue;
      // Still being probed (duration / dimensions) — neither pass nor fail, but say so
      // rather than let the file disappear from the list.
      if (it.checking) { pending.push(it); continue; }
      const r = fileFitsAsset(it, a);
      if (r.ok) pass.push(it); else fail.push({ item: it, reason: r.reason });
    }
    return { pass, fail, pending };
  };
  const assetSatisfied = (a: any): boolean => a.type === 'logo' ? true : assetFit(a).pass.length >= (a.count_min || 1);
  // Does an uploaded file satisfy THIS format for an asset-backed input? isInputFilled
  // only knows the bucket holds a file, which is how a video read green up in "this
  // format uses" while the platform's own spec rejected it in the materials list.
  const inputFitsFormat = (f: any, k: string): boolean => {
    if (!ASSET_INPUT_KEYS.has(k)) return isInputFilled(k);
    const wantsVideo = k === 'video';
    const cands = (f.assets || []).filter((a: any) => a.type !== 'logo'
      && (a.accepts || []).includes('VIDEO') === wantsVideo);
    if (!cands.length) return isInputFilled(k);
    return cands.some((a: any) => assetFit(a).pass.length > 0);
  };
  // Required real-asset specs for a format (logo is a bundled file, not user-required).
  const requiredAssets = (f: any): any[] => (f.assets || []).filter((a: any) => a.required && a.type !== 'logo');
  // Non-asset (text/keyword/youtube) inputs a format needs.
  const textInputs = (f: any): any[] => (f.inputs || []).filter((i: any) => !ASSET_INPUT_KEYS.has(i.key) && i.key !== 'logo');

  // Fill (0..1) — share of this format's requirements (assets + texts, incl. optional)
  // provided, judged per-format. Lights the dot green/partial/empty.
  const fmtFill = (f: any): number => {
    const assets = (f.assets || []).filter((a: any) => a.type !== 'logo');
    const texts = textInputs(f);
    const total = assets.length + texts.length;
    if (!total) return 0;
    const got = assets.filter(assetSatisfied).length + texts.filter((i: any) => isInputFilled(i.key)).length;
    return got / total;
  };

  // Ready = Landing URL + every REQUIRED asset satisfied by a spec-passing file +
  // every REQUIRED text filled. Optional never required.
  const fmtReady = (f: any): boolean => {
    // Every format needs a landing URL; the lead format ALSO needs a form attached
    // (Meta requires both — an external URL on the creative + the instant form as the CTA).
    const destOk = !!landingUrl.trim() && (f.id !== 'meta_lead' || !!leadFormId) &&
      (f.id !== 'tiktok_lead' || !!tiktokLeadFormId);
    return destOk &&
      requiredAssets(f).every(assetSatisfied) &&
      textInputs(f).filter((i: any) => i.required).every((i: any) => isInputFilled(i.key));
  };

  // A format can be GATED (greyed, not launchable) independent of its materials — returns the
  // reason to show, or null when available. Demand Gen: only with the Maximize-Conversions
  // objective, and only when the Google account has conversion tracking set up.
  const fmtGate = (f: any): string | null => {
    if (!f) return null;
    if (f.objective === 'conversions' && bidStrategy !== 'CPA')
      return t('max.gateConversionsOnly');
    if (f.requires === 'google_conversions' && caps.google_conversions === false)
      return t('max.gateNoConversions');
    return null;
  };

  // Count of launch-ready formats per platform — feeds the live budget preview (each ready
  // format is its own campaign, so a platform's minimum scales with this count).
  const readyCounts: Record<string, number> = {};
  (catalog?.platforms || []).forEach((pl: any) => {
    const n = (((catalog?.by_platform || {})[pl.id]) || []).filter((f: any) => fmtReady(f) && !disabledFormats.has(f.id) && !fmtGate(f)).length;
    if (n > 0) readyCounts[pl.id] = n;
  });
  const readyKey = JSON.stringify(readyCounts);

  // Blacklist. Sense carries it as an excluded Sense channel (same as a normal
  // Sense campaign), Google as negative placements, Yandex as campaign
  // ExcludedSites. Meta, TikTok and LinkedIn each need an asset uploaded to the
  // account beforehand — a block list, Pangle bundle ids, a publisher file — so a
  // launch cannot set them and the note says so.
  const blacklistNote = (): string | null => {
    // Domains come from the picked lists (plus anything an older draft typed in).
    const fromLists = blLists
      .filter(l => blSelected.includes(l.channel_id))
      .reduce((n, l) => n + (Number(l.sites_count) || 0), 0);
    const typed = blacklist.split(/[\s,;]+/).map(d => d.trim()).filter(Boolean).length;
    const domains = { length: fromLists + typed };
    if (!domains.length) return null;
    const platName = (id: string) =>
      ((catalog?.platforms || []).find((p: any) => p.id === id) || {}).name || id;
    // Which channels the launch can really exclude on — confirmed live on each one.
    const enforced: string[] = [], unsupported: string[] = [];
    Object.keys(readyCounts).forEach(id => {
      (['sense', 'google_ads', 'yandex'].includes(id) ? enforced : unsupported).push(platName(id));
    });
    const parts: string[] = [];
    if (enforced.length) parts.push(t('max.noteBlExcluded', { count: domains.length, where: enforced.join(', ') }));
    if (unsupported.length) parts.push(t('max.noteBlUnsupported', { where: unsupported.join(', ') }));
    return parts.join('; ') + '.';
  };

  // Devices. The note mirrors the adapters exactly, so it never promises more
  // than the launch will do:
  //   Sense    — targets device types directly.
  //   Google   — switches the un-wanted ones off with a -100% bid adjustment.
  //   Yandex   — same idea, except its desktop adjustment also covers tablets and
  //              cannot be combined with the tablet one, so "tablet without
  //              desktop" is left unrestricted there.
  //   Meta     — knows only mobile vs desktop (tablets ride with mobile), so a
  //              mixed pick like desktop+tablet restricts nothing.
  //   TikTok, LinkedIn — no device targeting at all.
  const deviceNote = (): string | null => {
    if (!devices.length || devices.length === DEVICES.length) return null;
    const platName = (id: string) =>
      ((catalog?.platforms || []).find((p: any) => p.id === id) || {}).name || id;
    const has = (d: string) => devices.includes(d);
    const off = DEVICES.filter(d => !has(d)).map(tv).join(', ');
    const yandexOk = !(has('Tablet') && !has('Computer'));
    // Meta restricts truthfully only when the pick maps onto one of its two
    // buckets: Computer alone → desktop, phone+tablet → mobile. "Tablet only" or
    // "phone only" would still serve the other, so it is not a restriction there.
    const metaOk = (has('Computer') && !has('Smart Phone') && !has('Tablet'))
      || (!has('Computer') && has('Smart Phone') && has('Tablet'));

    const enforced: string[] = [], missing: string[] = [];
    Object.keys(readyCounts).forEach(id => {
      const ok = id === 'sense' || id === 'google_ads'
        || (id === 'yandex' && yandexOk) || (id === 'meta' && metaOk);
      (ok ? enforced : missing).push(platName(id));
    });
    const parts: string[] = [];
    if (enforced.length) parts.push(t('max.noteSwitchedOffOn', { what: off, where: enforced.join(', ') }));
    if (missing.length) parts.push(t('max.noteNoDeviceRestriction', { where: missing.join(', ') }));
    return parts.length ? parts.join('; ') + '.' : null;
  };

  // Say what the choice switches OFF and where. Sense can drop the unwanted
  // environment per impression; Google can only drop apps (it has no positive
  // "browsers only" criterion); the rest have no web/app dimension at all, so
  // they are named as such rather than left to look enforced.
  const placementNote = (): string | null => {
    if (placementEnv.length !== 1) return null;      // both / none = no restriction
    const webOnly = placementEnv[0] === 'Web';
    const platName = (id: string) =>
      ((catalog?.platforms || []).find((p: any) => p.id === id) || {}).name || id;
    const enforced: string[] = [], missing: string[] = [];
    Object.keys(readyCounts).forEach(id => {
      if (id === 'sense' || (id === 'google_ads' && webOnly)) enforced.push(platName(id));
      else missing.push(platName(id));
    });
    const parts: string[] = [];
    if (enforced.length) {
      parts.push(t('max.noteSwitchedOffOn', {
        what: t(webOnly ? 'max.noteApps' : 'max.noteBrowsers'), where: enforced.join(', '),
      }));
    }
    if (missing.length) parts.push(t('max.noteNoSuchSetting', { where: missing.join(', ') }));
    return parts.length ? parts.join('; ') + '.' : null;
  };

  // Debounced budget-split preview: shows per-platform share + last-resort exclusions BEFORE
  // launch, so the user can raise the budget rather than discover it on the dashboard.
  useEffect(() => {
    const counts = JSON.parse(readyKey) as Record<string, number>;
    if (!selectedAdvertiserId || !budget || Number(budget) <= 0 || Object.keys(counts).length === 0) {
      setBudgetPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        // Only send a manual split whose channels are all still launch-ready — a stale channel
        // would skew the fractions. If none survive, fall back to the auto split.
        let split: Record<string, number> | null = null;
        if (budgetSplit) {
          const kept = Object.fromEntries(
            Object.entries(budgetSplit).filter(([c]) => counts[c] != null));
          const sum = Object.values(kept).reduce((a, b) => a + b, 0);
          if (sum > 0) split = Object.fromEntries(
            Object.entries(kept).map(([c, v]) => [c, v / sum]));   // renormalise to Σ=1
        }
        const r = await api.post('/api/sense-max/budget/preview', {
          advertiser_id: selectedAdvertiserId, budget: Number(budget), channels: counts, split,
        });
        setBudgetPreview(r.data);
      } catch { setBudgetPreview(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [budget, selectedAdvertiserId, readyKey, budgetSplit]);

  // Missing = destination (Landing URL, or a Lead form for meta_lead) + required assets
  // with no passing file + required texts.
  const fmtMissing = (f: any): string[] => {
    const m: string[] = [];
    if (!landingUrl.trim()) m.push(t('max.reqLandingUrl'));
    if (f.id === 'meta_lead' && !leadFormId) m.push(t('max.reqLeadForm'));
    if (f.id === 'tiktok_lead' && !tiktokLeadFormId) m.push(t('max.reqLeadForm'));
    requiredAssets(f).forEach((a: any) => {
      if (assetSatisfied(a)) return;
      // "Video" when nothing was uploaded, "Video (off-spec)" when a file is there
      // but this platform won't take it — the two used to read identically.
      const fit = assetFit(a);
      m.push(fit.pass.length === 0 && fit.fail.length > 0
        ? t('max.offSpecSuffix', { label: assetLabel(a) }) : assetLabel(a));
    });
    textInputs(f).filter((i: any) => i.required && !isInputFilled(i.key))
      .forEach((i: any) => m.push(inputLabel(i.key, i.label)));
    return m;
  };

  // Short value summary for a format input — what content actually feeds this format.
  const inputDisplay = (k: string): string => {
    if (k === 'keywords') { const n = parseKw(kwText).length; return n ? t('max.nKeywords', { count: n }) : '—'; }
    if (k === 'youtube_url') return ytLines.length ? t('max.nLinks', { count: ytLines.length }) : '—';
    if (k === 'logo') return t('max.bundledLogo');
    if (k.startsWith('image')) {
      const RB = ['image_square', 'image_portrait', 'image_landscape', 'image_vertical'];
      const keys = RB.includes(k) ? [k] : RB;   // count only this ratio's bucket
      const n = keys.reduce((s, ik) => s + (cUploaded[ik] || []).length, 0);
      return n ? t('max.nImages', { count: n }) : '—';
    }
    if (k === 'banner' || k === 'video') { const n = (cUploaded[k] || []).length; return n ? t('max.nFiles', { count: n }) : '—'; }
    const v = pvText(k);
    return v ? (v.length > 40 ? v.slice(0, 40) + '…' : v) : '—';
  };

  const renderMockup = (f: any) => {
    const sk = (w: string) => <span className="mk-skel" style={{ width: w }} />;
    const headline = pvText('headline');
    const desc = pvText('description') || pvText('body_text');
    const body = pvText('body_text') || pvText('description');
    const brand = pvBrand();
    const dom = landingUrl.trim() ? pvDomain() : '';
    // Logo is a fixed, hidden field — always the bundled file, never uploaded.
    // Edition-aware: a white-label shows its own mark, not Sense's.
    const logo = brandMark();
    const cta = cTexts['cta'] || t('max.cta_LearnMore');
    const usesCta = (f.inputs || []).some((i: any) => i.key === 'cta'); // only draw a CTA for formats that actually use one
    switch (f.mockup) {
      case 'search': {
        const hs = [...pvAll('headline'), pvText('headline2')].filter(Boolean).slice(0, 3);
        const ds = pvAll('description').slice(0, 2);
        return (
          <div className="mk mk-search">
            <div className="mk-srch-top"><span className="mk-ad">Ad</span><span className="mk-srch-url">{dom || sk('40%')}</span></div>
            <div className="mk-srch-title">{hs.length ? hs.join('  |  ') : sk('80%')}</div>
            <div className="mk-srch-desc">{ds.length ? ds.join(' ') : <>{sk('100%')}{sk('55%')}</>}</div>
          </div>
        );
      }
      case 'display':
        return (
          <div className="mk mk-display">
            {pvBannerNode() || <div className="mk-ph" />}
          </div>
        );
      case 'image': {
        // Pure static graphic banner (e.g. Yandex Image ad) — the image IS the ad. No
        // HTML5, no headline/CTA chrome; just show the uploaded static image.
        const bimg = pvImg('image_square', 'image_landscape', 'image_portrait', 'image');
        return (
          <div className="mk mk-display">
            {bimg ? <img src={bimg} alt="" /> : <div className="mk-ph" />}
          </div>
        );
      }
      case 'responsive': {
        const img = pvImg('image_landscape', 'image_square', 'image_portrait');
        const longH = pvText('long_headline');
        return (
          <div className="mk mk-resp">
            <div className="mk-resp-media">{img ? <img src={img} alt="" /> : <div className="mk-ph" />}</div>
            <div className="mk-resp-body">
              {logo && <img className="mk-logo" src={logo} alt="" />}
              <div className="mk-resp-long">{longH || sk('85%')}</div>
              <div className="mk-resp-h">{headline || sk('60%')}</div>
              <div className="mk-resp-d">{desc || sk('90%')}</div>
              <div className="mk-resp-foot"><span className="mk-brand">{brand || sk('45%')}</span>{usesCta && <span className="mk-cta">{cta}</span>}</div>
            </div>
          </div>
        );
      }
      case 'feed': {
        const isVideo = String(f.id).includes('video');
        const isLinkedin = f.platform === 'linkedin';
        const vid = isVideo && (cUploaded['video'] || []).some(x => x.file);
        const media = isVideo ? null
          : (isLinkedin ? pvImg('image_landscape', 'image_square', 'image_portrait')
                        : pvImg('image_square', 'image_portrait', 'image_landscape'));
        const hasMedia = !!media || vid;
        return (
          <div className="mk mk-feed">
            <div className="mk-feed-top">
              {logo ? <img className="mk-av" src={logo} alt="" /> : <div className="mk-av mk-av-ph" />}
              <div className="mk-min0"><div className="mk-brand">{brand || sk('60%')}</div><div className="mk-spon">{t(isLinkedin ? 'max.mockPromoted' : 'max.mockSponsored')}</div></div>
            </div>
            <div className="mk-feed-text">{body || <>{sk('100%')}{sk('70%')}</>}</div>
            <div className={`mk-feed-media ${hasMedia ? '' : 'mk-ph'} ${vid ? 'mk-vid' : ''}`}>
              {media ? <img src={media} alt="" /> : null}
              {vid && <span className="mk-play" />}
            </div>
            <div className="mk-feed-foot">
              <div className="mk-min0">
                <div className="mk-url">{dom || sk('40%')}</div>
                <div className="mk-h">{headline || sk('70%')}</div>
                {pvText('description') && <div className="mk-feed-desc">{pvText('description')}</div>}
              </div>
              {usesCta && <span className="mk-cta">{cta}</span>}
            </div>
          </div>
        );
      }
      case 'carousel': {
        const cards = (cUploaded['image_square'] || cUploaded['banner'] || []).filter(x => x.url).slice(0, 4);
        const ch = cMulti['card_headline'] || [];
        // Caption from the format's OWN text field (TikTok=ad_text, Meta=primary_text), NOT the
        // shared body_text — otherwise another platform's copy bleeds into this preview.
        const capKey = (f.inputs || []).map((i: any) => i.key).find((k: string) => ['ad_text', 'primary_text', 'body_text'].includes(k));
        const capt = capKey ? pvText(capKey) : '';
        return (
          <div className="mk mk-feed">
            <div className="mk-feed-top">
              {logo ? <img className="mk-av" src={logo} alt="" /> : <div className="mk-av mk-av-ph" />}
              <div className="mk-min0"><div className="mk-brand">{brand || sk('60%')}</div><div className="mk-spon">{t('max.mockSponsored')}</div></div>
            </div>
            <div className="mk-feed-text">{capt || <>{sk('100%')}{sk('70%')}</>}</div>
            <div className="mk-carousel">
              {cards.length
                ? cards.map((c, i) => (<div className="mk-card-item" key={i}><img src={c.url as string} alt="" />{ch[i] && <div className="mk-card-h">{ch[i]}</div>}</div>))
                : <><div className="mk-cardph" /><div className="mk-cardph" /><div className="mk-cardph" /></>}
            </div>
          </div>
        );
      }
      case 'vertical': {
        const isVideo = String(f.id).includes('video');
        const vid = isVideo && (cUploaded['video'] || []).some(x => x.file);
        const media = isVideo ? null : pvImg('image_portrait', 'image_square', 'image_landscape');
        const vText = pvText('ad_text');
        return (
          <div className="mk mk-vert">
            {media ? <img className="mk-vert-bg" src={media} alt="" /> : <div className="mk-vert-bg mk-ph">{vid && <span className="mk-play" />}</div>}
            <div className="mk-vert-ov">
              <div className="mk-brand mk-on-dark">{brand || sk('40%')}</div>
              <div className="mk-vert-text">{vText || <>{sk('90%')}{sk('60%')}</>}</div>
              {usesCta && <span className="mk-cta mk-cta-pill">{cta}</span>}
            </div>
          </div>
        );
      }
      case 'video': {
        const yt = pvYt();
        const vid = (cUploaded['video'] || []).some(x => x.file);
        const thumb = yt && yt.thumbnail;
        const has = !!thumb || vid;
        // The text footer only makes sense for formats that carry a headline / CTA
        // (YouTube, Yandex video). Plain Sense video has none → just the player.
        const usesHeadline = (f.inputs || []).some((i: any) => i.key === 'headline');
        const hasMeta = usesHeadline || usesCta || !!(yt && (yt.title || yt.author));
        return (
          <div className="mk mk-video">
            <div className={`mk-video-thumb ${has ? '' : 'mk-ph'}`}>
              {thumb ? <img src={thumb} alt="" /> : null}
              {has && <span className="mk-play" />}
            </div>
            {hasMeta && (
              <div className="mk-video-meta">
                <div className="mk-min0">
                  <div className="mk-h">{(yt && yt.title) || headline || sk('70%')}</div>
                  <div className="mk-url">{(yt && yt.author) || dom || sk('40%')}</div>
                </div>
                {usesCta && <span className="mk-cta">{cta}</span>}
              </div>
            )}
          </div>
        );
      }
      default:
        return <div className="mk mk-display"><div className="mk-ph" /></div>;
    }
  };

  if (isAllClients) return <ChooseClientPlaceholder pageName="Sense Max" />;

  // Sense Max is available only for clients with an external-platform integration.
  if (!selectedAdvertiser?.has_external_integration) {
    return (
      <ChooseClientPlaceholder
        title={t('max.unavailableTitle')}
        hideButton
        text={t('max.unavailableText')}
      />
    );
  }

  const launchMiss = launchMissing();   // what's still blocking launch (empty = ready)
  const manageMiss = isManage ? manageMissing() : [];   // launched formats missing materials
  // Landing reachable but its text can't be read (hard bot protection) — the site
  // is fine for the campaign, but AI copy generation is impossible → hide Generate.
  const genBlocked = !!urlInfo && urlInfo.ok && urlInfo.can_generate === false;

  return (
    <div className="page-container sense-max-page">
      <a href="#" className="back-link" onClick={(e) => { e.preventDefault(); navigate('/campaigns'); }}>
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" /></svg>
        {t('max.backToCampaigns')}
      </a>

      <header className="edit-header">
        <div className="header-title">
          <h1>{whiteLabel
            ? <>{brandName} <span className="omni-badge">omni</span></>
            : <>Sense Max <span className="sm-title-badge">MAX</span></>}</h1>
          <p className="header-subtitle">{t(isManage
            ? 'max.subtitleManage' : 'max.subtitleLaunch')}</p>
        </div>
      </header>

      <div className="edit-layout">
        {/* Left — Campaign Details */}
        <aside className="campaign-panel">
          <div className="panel-section campaign-details-section">
            <div className="section-header">
              <h2 className="section-title">{t('max.campaignDetails')}</h2>
              {!isManage && (
                <div className="form-actions">
                  <button className="btn-icon" title={t('max.duplicateCampaign')} onClick={() => alert(t('max.phaseStub'))}>
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" /></svg>
                  </button>
                  <button className="btn-icon danger" title={t('max.deleteCampaign')} onClick={() => alert(t('max.phaseStub'))}>
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                  </button>
                </div>
              )}
            </div>

            <div className="form-field">
              <label className="field-label">{t('max.campaignName')}</label>
              <input type="text" className={`field-input ${triedSave && !campaignName.trim() ? 'sm-required-empty' : ''}`} value={campaignName} onChange={e => setCampaignName(e.target.value)} />
            </div>

            {/* Flight & status are managed on the campaign dashboard — hidden when editing
                creatives/texts of a launched campaign. */}
            {!isManage && (<>
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">{t('max.startDate')}</label>
                <input type="date" className={`field-input ${triedSave && !startDate ? 'sm-required-empty' : ''}`} value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="form-field">
                <label className="field-label">{t('max.endDate')}</label>
                <input type="date" className={`field-input ${triedSave && !endDate ? 'sm-required-empty' : ''}`} value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate || undefined} />
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">{t('max.status')}</label>
              <div className="status-toggle">
                <button className={`status-btn ${campaignStatus === 'active' ? 'active' : ''}`} onClick={() => setCampaignStatus('active')}>
                  <span className="status-dot live" /> {t('max.statusActive')}
                </button>
                <button className={`status-btn ${campaignStatus === 'paused' ? 'active' : ''}`} onClick={() => setCampaignStatus('paused')}>
                  ⏸ {t('max.statusPaused')}
                </button>
              </div>
            </div>
            </>)}

            <div className="form-actions-bottom">
              <button className="btn-secondary-sm" onClick={() => isManage ? navigate(-1) : navigate('/campaigns')}>{t('common.cancel')}</button>
              {/* Draft-only save exists for the builder flow; in manage mode there's a single
                  "Save changes" that also publishes the new creatives/texts. */}
              {!isManage && (
                <button className={`btn-primary ${saving ? 'saving' : ''}`}
                  disabled={saving || launching}
                  onClick={handleSaveDraft}>
                  {saving ? t('max.savingShort')
                    : savedFlash ? t('max.savedFlash')
                      : t(draftId ? 'max.saveChanges' : 'max.saveCampaign')}
                </button>
              )}
              {isManage ? (
                <span className="sm-launch-wrap"
                  data-hint={manageMiss.length ? t('max.hintAddMaterials', { items: manageMiss.join(', ') }) : undefined}>
                  <button className={`btn-publish ${launching ? 'publishing' : ''}`}
                    disabled={saving || launching || manageMiss.length > 0}
                    onClick={handleApplyChanges}>
                    {(saving || launching)
                      ? (<><span className="btn-spinner"></span>{t('max.savingShort')}</>)
                      : t('max.saveChanges')}
                  </button>
                </span>
              ) : (
                <span className="sm-launch-wrap"
                  data-hint={launchMiss.length ? t('max.hintNeeds', { items: launchMiss.join(', ') }) : undefined}>
                  <button className={`btn-publish ${launching ? 'publishing' : ''}`}
                    disabled={saving || launching || launchMiss.length > 0}
                    onClick={() => setConfirmLaunch(true)}>
                    {launching
                      ? (<><span className="btn-spinner"></span>{t('max.launching')}</>)
                      : t('max.saveAndLaunch')}
                  </button>
                </span>
              )}
            </div>
          </div>
        </aside>

        {/* Right — Edit Ad Group */}
        <div className="adgroup-panel">
          <div className="adgroup-form">
          {/* Budget, bidding, targeting, day&time — hidden in manage mode (creatives/texts
              only; targeting isn't editable after launch). */}
          {!isManage && (<>
          <div className="form-header">
            <h2 className="form-title">{t('max.campaignSettings')}</h2>
          </div>

          {/* Budget & Bidding */}
          <div className="form-section">
            <div className="form-row form-row-3">
              <div className="form-field">
                <label className="field-label">{t('max.budget')}</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">{currency}</span>
                  <input type="text" inputMode="decimal" className="field-input budget-input" value={budget} onChange={e => setBudget(decimalOnly(e.target.value))} />
                </div>
                {user?.role === 'admin' && currency !== 'EUR' && budget && Number(budget) > 0 && (
                  <div className="field-hint" style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                    ~ {(Number(budget) / currencyRate).toFixed(2)} EUR
                  </div>
                )}
              </div>
              <div className="form-field">
                <label className="field-label">{t('max.strategy')}</label>
                <select className="field-select" value={bidStrategy}
                  onChange={e => { setBidStrategy(e.target.value); if (e.target.value !== 'CPA') setOptimizationGoalIds([]); }}>
                  <option value="AV_VIEWED">{t('max.stratViewable')}</option>
                  <option value="CPC">{t('max.stratClicks')}</option>
                  {/* CPA needs a conversion pixel — only OFFER it when the client has one; keep
                      it selectable if a draft already has it (so the select isn't inconsistent). */}
                  {(convActivities.length > 0 || bidStrategy === 'CPA') &&
                    <option value="CPA">{t('max.stratCpa')}</option>}
                </select>
                {bidStrategy === 'CPA' && (
                  <div style={{ marginTop: 8 }}>
                    <label className="field-label" style={{ fontSize: 12 }}>{t('max.conversionActivity')}</label>
                    <select className="field-select" value={optimizationGoalIds[0] || ''}
                      onChange={e => setOptimizationGoalIds(e.target.value ? [e.target.value] : [])}>
                      <option value="">{t('max.selectConversion')}</option>
                      {convActivities.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    {!optimizationGoalIds.length && (
                      <div className="field-hint" style={{ color: '#E94B6C', fontSize: 12, marginTop: 4 }}>
                        {t('max.pickConversionCpa')}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label className="field-label">{t('max.targetKpi', { kpi: kpiLabel })}</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">{currency}</span>
                  <input type="text" inputMode="decimal" className="field-input" value={kpiAmount} placeholder="0.00" onChange={e => setKpiAmount(decimalOnly(e.target.value))} />
                </div>
                {user?.role === 'admin' && currency !== 'EUR' && kpiAmount && Number(kpiAmount) > 0 && (
                  <div className="field-hint" style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                    ~ {(Number(kpiAmount) / currencyRate).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} EUR
                  </div>
                )}
              </div>
            </div>

            {budgetPreview && ((budgetPreview.allocation || []).length > 0 || (budgetPreview.excluded || []).length > 0) && (
              <div className="budget-preview">
                <div className="budget-preview-title">{t('max.budgetSplitTitle')}</div>
                {(budgetPreview.allocation || []).length > 0 && (
                  <BudgetSplitBar
                    alloc={budgetPreview.allocation}
                    total={budgetPreview.total || 0}
                    currency={budgetPreview.currency}
                    senseMin={budgetPreview.sense_min_share ?? 0.15}
                    split={budgetSplit}
                    onChange={setBudgetSplit}
                  />
                )}
                {(budgetPreview.excluded || []).length > 0 && (
                  <div className="bp-excluded">
                    {budgetPreview.excluded.map((e: any) => (
                      <div key={e.channel} className="bp-excl-row">
                        <span className="bp-excl-dot" />
                        <span><strong>{t('max.wontLaunch', { platform: e.platform })}</strong> {e.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
                {budgetSplit && (
                  <button type="button" className="bp-reset" onClick={() => setBudgetSplit(null)}>
                    {t('max.budgetSplitReset', 'Reset to automatic split')}
                  </button>
                )}
              </div>
            )}

            <div className="form-row-flex">
              <div className="form-field form-field-auto">
                <label className="field-label">{t('max.freqLimit')}</label>
                <div className="frequency-input">
                  <input type="text" inputMode="numeric" className="field-input frequency-count" value={freqCount} onChange={e => setFreqCount(numOnly(e.target.value))} />
                  <span className="frequency-text">{t('max.impressionsPer')}</span>
                  <select className="field-select frequency-period" value={freqPeriod} onChange={e => setFreqPeriod(e.target.value)}>
                    <option value="day">{t('max.periodDay')}</option><option value="week">{t('max.periodWeek')}</option>
                    <option value="month">{t('max.periodMonth')}</option><option value="lifetime">{t('max.periodLifetime')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Targeting */}
          <div className="form-section">
            <h3 className="form-section-title">{t('max.targeting')}</h3>
            <div className="targeting-group"><div className="targeting-row">
              <label className="targeting-label">{t('max.age')}</label>
              <div className="targeting-tags">{AGE_RANGES.map(a => Tag(ages.length === 0 || ages.includes(a), a, () => setAges(toggle(ages, a))))}</div>
            </div></div>
            <div className="targeting-group"><div className="targeting-row">
              <label className="targeting-label">{t('max.gender')}</label>
              <div className="targeting-tags">{GENDERS.map(g => Tag(genders.length === 0 || genders.includes(g), g, () => setGenders(toggle(genders, g))))}</div>
            </div></div>
            <div className="targeting-group"><div className="targeting-row">
              <label className="targeting-label">{t('max.parentalStatus')}</label>
              <div className="targeting-tags">{PARENTAL.map(p => Tag(parental.length === 0 || parental.includes(p), p, () => setParental(toggle(parental, p))))}</div>
            </div></div>
            <div className="targeting-group"><div className="targeting-row">
              <label className="targeting-label">{t('max.devices')}</label>
              <div className="targeting-tags">{DEVICES.map(d => Tag(devices.length === 0 || devices.includes(d), d, () => setDevices(toggle(devices, d))))}</div>
            </div>
            {deviceNote() && <div className="targeting-hint">{deviceNote()}</div>}
            </div>
            <div className="targeting-group"><div className="targeting-row">
              <label className="targeting-label">{t('max.placement')}</label>
              <div className="targeting-tags">
                {PLACEMENT_ENVS.map(e => Tag(
                  placementEnv.length === 0 || placementEnv.includes(e), e,
                  // Turning the last one off would mean "nowhere", so an empty
                  // selection is read as both — same rule as the rows above.
                  () => setPlacementEnv(placementEnv.length === 1 && placementEnv[0] === e
                    ? [] : toggle(placementEnv, e))))}
              </div>
            </div>
            {placementNote() && <div className="targeting-hint">{placementNote()}</div>}
            </div>
            <div className="targeting-group"><div className="targeting-row">
              <label className="targeting-label">{t('max.blacklist')}</label>
              <div className="sm-bl-picker">
                {/* The client's saved URL lists — the same ones the campaign editor
                    uses, so a list built there is available here and vice versa. */}
                <div className="proximity-saved-row">
                  {blLists.map(l => {
                    const on = blSelected.includes(l.channel_id);
                    return (
                      <div key={l.channel_id} className={`proximity-chip ${on ? 'selected' : ''}`}>
                        <span className={`chip-checkbox ${on ? 'checked' : ''}`}
                          onClick={() => setBlSelected(on
                            ? blSelected.filter(id => id !== l.channel_id)
                            : [...blSelected, l.channel_id])}>{on && '✓'}</span>
                        <span className="chip-label">{l.name}</span>
                        <span className="proximity-chip-count">{l.sites_count}</span>
                      </div>
                    );
                  })}
                  <button type="button" className="proximity-chip proximity-chip-new"
                    onClick={() => { setBlNewOpen(!blNewOpen); setBlNewErr(null); }}>{t('max.newChip')}</button>
                </div>
                {blLists.length === 0 && !blNewOpen && (
                  <div className="targeting-hint">{t('max.noSavedLists')}</div>
                )}
                {blNewOpen && (
                  <div className="proximity-editor">
                    <input className="sm-input" type="text" placeholder={t('max.listName')}
                      value={blNewName} onChange={e => setBlNewName(e.target.value)} />
                    <textarea className="sm-blacklist" rows={3}
                      placeholder={'example.com\nnews.example.org'}
                      value={blNewSites} onChange={e => setBlNewSites(e.target.value)} />
                    {blNewErr && <div className="targeting-hint sm-bl-err">{blNewErr}</div>}
                    <div className="sm-bl-actions">
                      <button type="button" className="proximity-save-btn"
                        disabled={blSaving || !blNewName.trim() || !blNewSites.trim()}
                        onClick={saveNewBlacklist}>{t(blSaving ? 'max.savingShort' : 'max.saveListBtn')}</button>
                      <button type="button" className="proximity-inline-cancel"
                        onClick={() => { setBlNewOpen(false); setBlNewErr(null); }}>✕</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {blacklistNote() && <div className="targeting-hint">{blacklistNote()}</div>}
            </div>
            <div className="targeting-group">
              <div className="geo-columns">
                {geoColumn(false, geoIncluded, (id) => setGeoIncluded(geoIncluded.filter(g => g.id !== id)), geoIn, setGeoIn, geoLoad, geoRes, selectGeo)}
                {geoColumn(true, geoExcluded, (id) => setGeoExcluded(geoExcluded.filter(g => g.id !== id)), geoExIn, setGeoExIn, geoExLoad, geoExRes, selectGeoEx)}
              </div>
            </div>
          </div>

          {/* Advanced Targeting */}
          <div className="form-section form-section-collapsible">
            <div className="form-section-header" onClick={() => setShowAdvanced(!showAdvanced)}>
              <h3 className="form-section-title">{t('max.advancedTargeting')}</h3>
              <svg className={`form-section-chevron ${showAdvanced ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </div>
            {showAdvanced && (
              <AdvancedTargeting selectedItems={advanced} onSelectionChange={setAdvanced} />
            )}
          </div>

          {/* Day & Time */}
          <div className="form-section form-section-collapsible">
            <div className="form-section-header" onClick={() => setShowDayTime(!showDayTime)}>
              <h3 className="form-section-title">Day &amp; Time</h3>
              <svg className={`form-section-chevron ${showDayTime ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </div>
            {showDayTime && (
            <div className="day-time-section">
              <div className="day-time-header">
                <div className="day-time-actions">
                  <button className="day-time-action-link"
                    onClick={() => setDayTime(new Set(ALL_DAYS.flatMap(d => Array.from({ length: 24 }, (_, h) => `${d}-${h}`))))}>{t('max.selectAll')}</button>
                  <button className="day-time-action-link" onClick={() => setDayTime(new Set())}>{t('max.clearAll')}</button>
                </div>
              </div>
              <div className="day-time-grid">
                <div className="day-time-row day-time-header-row">
                  <div className="day-time-day-label" />
                  {Array.from({ length: 24 }, (_, h) => {
                    const colSel = ALL_DAYS.some(d => dayTime.has(`${d}-${h}`));
                    return (
                      <div key={h} className={`day-time-hour-label ${!colSel ? 'inactive' : ''}`}
                        onClick={() => {
                          const sel = ALL_DAYS.filter(d => dayTime.has(`${d}-${h}`)).length;
                          const clear = sel > ALL_DAYS.length / 2;
                          const ns = new Set(dayTime);
                          ALL_DAYS.forEach(d => clear ? ns.delete(`${d}-${h}`) : ns.add(`${d}-${h}`));
                          setDayTime(ns);
                        }}>{h}</div>
                    );
                  })}
                </div>
                {DAYS.map(day => {
                  const rowSel = Array.from({ length: 24 }, (_, h) => h).some(h => dayTime.has(`${day.key}-${h}`));
                  return (
                    <div key={day.key} className={`day-time-row ${day.weekend ? 'weekend' : ''} ${!rowSel ? 'inactive' : ''}`}>
                      <div className="day-time-day-label"
                        onClick={() => {
                          const sel = Array.from({ length: 24 }, (_, h) => h).filter(h => dayTime.has(`${day.key}-${h}`)).length;
                          const clear = sel > 12;
                          const ns = new Set(dayTime);
                          for (let h = 0; h < 24; h++) clear ? ns.delete(`${day.key}-${h}`) : ns.add(`${day.key}-${h}`);
                          setDayTime(ns);
                        }}>{tv(day.label)}</div>
                      {Array.from({ length: 24 }, (_, h) => {
                        const key = `${day.key}-${h}`;
                        const isSel = dayTime.has(key);
                        return (
                          <div key={h} className={`day-time-cell ${isSel ? 'selected' : ''}`}
                            onMouseDown={(e) => {
                              e.preventDefault(); setDragging(true);
                              dragMode.current = isSel ? 'deselect' : 'select';
                              const ns = new Set(dayTime);
                              dragMode.current === 'deselect' ? ns.delete(key) : ns.add(key);
                              setDayTime(ns);
                            }}
                            onMouseEnter={() => {
                              if (!dragging) return;
                              setDayTime(prev => {
                                const ns = new Set(prev);
                                dragMode.current === 'deselect' ? ns.delete(key) : ns.add(key);
                                return ns;
                              });
                            }} />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>

          </>)}

          {/* Lead form — destination for the Meta 'Lead form' format (shown whenever that
              format is available for this client, i.e. Meta is connected). */}
          {metaLeadAvailable && (
            <div className="sm-text" style={{ marginBottom: 14 }}>
              <label className="sm-text-label">
                <span>{t('max.leadFormMeta')}</span>
              </label>
              <select className="sm-input" value={leadFormId}
                onChange={e => setLeadFormId(e.target.value)} disabled={leadFormsBusy}>
                <option value="">{t(leadFormsBusy ? 'max.loadingForms' : 'max.selectLeadForm')}</option>
                {leadForms.map(f => (
                  <option key={f.id} value={f.id}>
                    {(f.name || t('max.untitledForm')) + ` · ${f.id}`
                      + (f.status && f.status !== 'ACTIVE' ? ` (${f.status})` : '')}
                  </option>
                ))}
              </select>
              {!leadFormsBusy && leadForms.length === 0 && (
                <span className="sm-text-hint" style={{ color: '#c0392b', marginTop: 4 }}>
                  {t('max.noMetaLeadForms')}
                </span>
              )}
            </div>
          )}

          {/* Lead form — destination for the TikTok 'Lead form' format (shown whenever
              TikTok is connected). Mirror of the Meta picker above. */}
          {tiktokLeadAvailable && (
            <div className="sm-text" style={{ marginBottom: 14 }}>
              <label className="sm-text-label">
                <span>{t('max.leadFormTiktok')}</span>
              </label>
              <select className="sm-input" value={tiktokLeadFormId}
                onChange={e => setTiktokLeadFormId(e.target.value)} disabled={tiktokLeadFormsBusy}>
                <option value="">{t(tiktokLeadFormsBusy ? 'max.loadingForms' : 'max.selectLeadForm')}</option>
                {tiktokLeadForms.map(f => (
                  <option key={f.id} value={f.id}>
                    {(f.name || t('max.untitledForm')) + ` · ${f.id}`
                      + (f.status && f.status !== 'ACTIVE' ? ` (${f.status})` : '')}
                  </option>
                ))}
              </select>
              {!tiktokLeadFormsBusy && tiktokLeadForms.length === 0 && (
                <span className="sm-text-hint" style={{ color: '#c0392b', marginTop: 4 }}>
                  {t('max.noTiktokLeadForms')}
                </span>
              )}
            </div>
          )}

          {/* Landing URL — the destination for every URL-based format (Sense/Google/… and
              Meta image/video). The lead form above is the destination for meta_lead only. */}
          <div className="sm-landing-section">
            <div className={`sm-landing ${!landingUrl.trim() ? 'is-empty' : ''}`}>
              <div className="sm-landing-head">
                <span className="sm-landing-ic" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                </span>
                <div className="sm-landing-txt">
                  <span className="sm-landing-title">{t('max.landingUrl')} <span className="req">*</span></span>
                  {landingUrl.trim()
                    ? <span className="sm-landing-sub">{t('max.landingSub')}</span>
                    : <span className="sm-landing-required">{t('max.landingRequired')}</span>}
                </div>
              </div>
              <input type="text" className={`field-input sm-landing-input ${!landingUrl.trim() ? 'sm-required-empty' : ''}`} value={landingUrl} placeholder="https://…" onChange={e => setLandingUrl(e.target.value)} />
              {(urlChecking || urlInfo) && (
                <div className={`sm-url-preview ${urlInfo ? (urlInfo.ok ? 'good' : 'bad') : ''}`}
                  style={genBlocked ? { borderColor: '#f59e0b', background: '#fff7ed' } : undefined}>
                  {urlChecking ? (
                    <span className="sm-url-checking">{t('max.checking')}</span>
                  ) : genBlocked ? (
                    <>
                      {urlInfo.favicon && (
                        <img className="sm-url-favicon" src={urlInfo.favicon} alt=""
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      )}
                      <div className="sm-url-meta">
                        <span className="sm-url-title">{urlInfo.title || urlInfo.final_url}</span>
                        <span className="sm-url-desc" style={{ color: '#b45309' }}>
                          {t('max.noTextAccessDesc')}
                        </span>
                      </div>
                      <span className="sm-url-badge" style={{ background: '#ffedd5', color: '#c2410c' }}>{t('max.noTextAccessBadge')}</span>
                    </>
                  ) : urlInfo?.ok ? (
                    <>
                      {urlInfo.favicon && (
                        <img className="sm-url-favicon" src={urlInfo.favicon} alt=""
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      )}
                      <div className="sm-url-meta">
                        <span className="sm-url-title">{urlInfo.title || urlInfo.final_url}</span>
                        {urlInfo.blocked
                          ? <span className="sm-url-desc">{t('max.reachableBlocked')}</span>
                          : (urlInfo.description && <span className="sm-url-desc">{urlInfo.description}</span>)}
                      </div>
                      <span className="sm-url-badge ok">{urlInfo.blocked ? t('max.badgeReachable') : '✓ OK'}</span>
                    </>
                  ) : (
                    <span className="sm-url-badge bad">
                      ✗ {urlInfo?.status
                        ? t('max.pageReturnsError', { status: urlInfo.status })
                        : (urlInfo?.error || t('max.unreachable'))}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Creatives — unique options across all platforms (enter once, applies to all) */}
          <div className="form-section form-section-collapsible">
            <div className="form-section-header" onClick={() => setShowCreatives(!showCreatives)}>
              <h3 className="form-section-title">{t('max.creatives')}</h3>
              <svg className={`form-section-chevron ${showCreatives ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </div>
            {showCreatives && (
              <div className="sm-creatives">
                <div className="sm-cr-group-title">{t('max.assets')} <span className="sm-cr-hint">{t('max.assetsHint')}</span></div>

                {/* Single unified upload zone — drop or browse any image, HTML5 (ZIP) or
                    video. Files are auto-classified and sorted into their format groups below. */}
                <div
                  className={`creatives-empty-clickable sm-cr-drop ${cDragging ? 'dragging' : ''}`}
                  onClick={() => cDropInput.current?.click()}
                  onDragOver={e => { e.preventDefault(); if (!cDragging) setCDragging(true); }}
                  onDragLeave={e => { e.preventDefault(); setCDragging(false); }}
                  onDrop={e => { e.preventDefault(); setCDragging(false); addBulk(e.dataTransfer.files); }}>
                  <input ref={cDropInput} type="file" multiple hidden accept="image/*,video/*,.zip"
                    onChange={e => { addBulk(e.target.files); (e.target as HTMLInputElement).value = ''; }} />
                  <div className="empty-icon">
                    <svg className="icon-default" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5-7l-3 3.72L9 13l-3 4h12l-4-5z" /></svg>
                    <svg className="icon-hover" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                  </div>
                  <div className="text-container">
                    <p className="text-default">{t('max.dropCreatives')}</p>
                    <p className="text-hover">{t('max.addCreatives')}</p>
                  </div>
                  <span className="subtext">{t('max.dropSubtext')}</span>
                </div>
                {bulkSummary && (
                  <div className="sm-bulk-summary">
                    {Object.entries(bulkSummary.distributed)
                      .map(([k, n]) => `${inputLabel(k, creativeOpts.assets.find(x => x.key === k)?.label)}: ${n}`)
                      .join(' · ') || t('max.nothingMatched')}
                    {(bulkSummary.duplicates || 0) > 0 &&
                      <span className="sm-bulk-rej">{t('max.skippedDuplicates', { count: bulkSummary.duplicates })}</span>}
                    {bulkSummary.rejected.length > 0 &&
                      <span className="sm-bulk-rej">{t('max.rejectedFiles', {
                        count: bulkSummary.rejected.length,
                        reasons: Array.from(new Set(bulkSummary.rejected.map(x => x.reason))).join(', '),
                      })}</span>}
                  </div>
                )}
                <div className="sm-asset-blocks">
                  {creativeOpts.assets.filter(a => (cUploaded[a.key] || []).length > 0).map(a => (
                    <div className="sm-asset-block" key={a.key}>
                      <label className="sm-text-label">
                        <span>{inputLabel(a.key, a.label)}{assetSpec(a) && <>{' '}<span className="sm-help">?<span className="sm-help-pop">{assetSpec(a)}</span></span></>}</span>
                      </label>
                      <div className="file-grid">
                        {(cUploaded[a.key] || []).map(it => (
                          <div className={`file-card ${it.issues.length ? 'sm-card-bad' : ''}`} key={it.id} title={it.issues.join('; ')}>
                            <div className="file-card-preview">
                              {it.url
                                ? <img src={it.url} alt={it.name} />
                                : <div className="file-card-type">{it.ext || 'FILE'}</div>}
                            </div>
                            <div className="file-card-info">
                              <span className="file-card-name">{it.name}</span>
                              {(() => {
                                const dw = it.html5?.w ?? it.w;
                                const dh = it.html5?.h ?? it.h;
                                return dw && dh
                                  ? <span className="file-card-size" style={{ fontSize: 11, color: '#8a8594', fontWeight: 600 }}>{dw}×{dh}</span>
                                  : null;
                              })()}
                              {it.checking
                                ? <span className="sm-card-issue checking">{t('max.checkingShort')}</span>
                                : (it.issues.length > 0 && <span className="sm-card-issue">{it.issues[0]}</span>)}
                            </div>
                            <button className="file-card-remove" title={t('max.remove')} onClick={() => removeCFile(a.key, it.id)}>✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* YouTube videos — a distinct bordered block (with the YT mark) so it
                    reads as its own creative source, not another text field. */}
                <div className="sm-yt-block">
                  <div className="sm-yt-head">
                    <span className="sm-yt-ic" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z" /></svg>
                    </span>
                    <div className="sm-yt-head-txt">
                      <span className="sm-yt-title">{t('max.ytTitle')}</span>
                      <span className="sm-yt-sub">{t('max.ytSub')}</span>
                    </div>
                  </div>
                  <textarea className="sm-input sm-yt-input" rows={3} value={ytText}
                    placeholder={t('max.ytPlaceholder')} onChange={e => onYtChange(e.target.value)} />
                  {ytLines.length > 0 && (
                    <div className="sm-yt-list">
                      {ytLines.map(u => {
                        const info = ytInfos[u];
                        return (
                          <div className={`sm-url-preview ${info && !info.checking ? (info.ok ? 'good' : 'bad') : ''}`} key={u}>
                            {(!info || info.checking) ? <span className="sm-url-checking">{t('max.checking')}</span>
                              : info.ok ? <>
                                  {info.thumbnail && <img className="sm-yt-thumb" src={info.thumbnail} alt=""
                                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                                  <div className="sm-url-meta">
                                    <span className="sm-url-title">{info.title || info.video_id}</span>
                                    {info.author && <span className="sm-url-desc">{info.author}</span>}
                                  </div>
                                  <span className="sm-url-badge ok">✓ OK</span>
                                </>
                                : <span className="sm-url-badge bad">✗ {info.error || t('max.notFound')}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Keywords — its own block (Search-only input), kept clear of the copy fields */}
                {(() => {
                  const kwDef = creativeOpts.texts.find(t => t.key === 'keywords');
                  if (!kwDef) return null;
                  const kws = parseKw(kwText);
                  return (
                    <div className="sm-kw-block">
                      <div className="sm-kw-head">
                        <span className="sm-kw-ic" aria-hidden>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
                        </span>
                        <div className="sm-kw-head-txt">
                          <span className="sm-kw-title">{t('max.keywords')}</span>
                          <span className="sm-kw-sub">{t('max.keywordsSub')}</span>
                        </div>
                        <span className="sm-kw-right">
                          <span className="sm-kw-count">{t('max.keywordsCount', { count: kws.length })}{kwDef.max_len ? t('max.keywordsMaxLen', { max: kwDef.max_len }) : ''}</span>
                          {landingUrl.trim() && !genBlocked && <button type="button" className="sm-gen" disabled={!!genBusy['keywords']}
                            onClick={e => { e.preventDefault(); genText(kwDef); }} title={t('max.generateFromPage')}>{genBusy['keywords'] ? <span className="sm-gen-spin" aria-hidden /> : <span className="sm-gen-spark" aria-hidden />}<span className="sm-gen-label">{t(genBusy['keywords'] ? 'max.generating' : 'max.generate')}</span></button>}
                        </span>
                      </div>
                      <textarea className="sm-input sm-kw-input" rows={5} value={kwText}
                        placeholder={t('max.kwPlaceholder')}
                        onChange={e => setKwText(e.target.value)} />
                      <div className="sm-kw-tools">
                        <label className="sm-kw-upload">
                          <input type="file" accept=".txt,.csv" hidden
                            onChange={e => { uploadKeywords(e.target.files); (e.target as HTMLInputElement).value = ''; }} />
                          {t('max.uploadKwList')}
                        </label>
                        {kws.length > 0 && <button type="button" className="sm-kw-clear" onClick={() => setKwText('')}>{t('max.clearAllShort')}</button>}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const KEY_TEXTS = ['headline', 'description', 'body_text', 'business_name'];
                  const HIDDEN_TEXTS = ['youtube_url', 'keywords'];
                  const renderText = (tx: any) => {
                    // CTA is a platform enum everywhere (Google/Meta/TikTok/LinkedIn) — never
                    // free text. Offer a common set; each platform maps to its nearest button at launch.
                    if (tx.key === 'cta') {
                      // `value` is the enum we map to each platform's button at launch —
                      // it stays English; only labelKey is interface text.
                      const CTA_OPTS = [
                        { v: 'Learn more', k: 'max.cta_LearnMore' }, { v: 'Shop now', k: 'max.cta_ShopNow' },
                        { v: 'Sign up', k: 'max.cta_SignUp' }, { v: 'Subscribe', k: 'max.cta_Subscribe' },
                        { v: 'Contact us', k: 'max.cta_ContactUs' }, { v: 'Get quote', k: 'max.cta_GetQuote' },
                        { v: 'Get offer', k: 'max.cta_GetOffer' }, { v: 'Download', k: 'max.cta_Download' },
                        { v: 'Book now', k: 'max.cta_BookNow' }, { v: 'Apply now', k: 'max.cta_ApplyNow' },
                        { v: 'Order now', k: 'max.cta_OrderNow' }, { v: 'Watch more', k: 'max.cta_WatchMore' },
                      ];
                      const v = cTexts['cta'] || '';
                      return (
                        <div className="sm-text" key={tx.key}>
                          <label className="sm-text-label">
                            <span>{inputLabel(tx.key, tx.label)}</span>
                            <span className="sm-text-hint">{t('max.ctaHint')}</span>
                          </label>
                          <select className="sm-input" value={v}
                            onChange={e => setCTexts(p => ({ ...p, cta: e.target.value }))}>
                            <option value="">{t('max.ctaNone')}</option>
                            {CTA_OPTS.map(o => <option key={o.v} value={o.v}>{t(o.k)}</option>)}
                          </select>
                        </div>
                      );
                    }
                    if (tx.multiline) {
                      const vals = cMulti[tx.key] || [];
                      const showTrailing = !tx.count_max || vals.length < tx.count_max;
                      const inputs = showTrailing ? [...vals, ''] : vals;
                      return (
                        <div className="sm-text" key={tx.key}>
                          <label className="sm-text-label">
                            <span>{inputLabel(tx.key, tx.label)}</span>
                            <span className="sm-text-right">
                              <span className="sm-text-hint">{tx.max_len ? t('max.eachMaxLen', { max: tx.max_len }) : ''}{tx.count_max ? t('max.upToCount', { count: tx.count_max }) : ''}</span>
                              {landingUrl.trim() && !genBlocked && <button type="button" className="sm-gen" disabled={!!genBusy[tx.key]}
                                onClick={e => { e.preventDefault(); genText(tx); }} title={t('max.generateFromPage')}>{genBusy[tx.key] ? <span className="sm-gen-spin" aria-hidden /> : <span className="sm-gen-spark" aria-hidden />}<span className="sm-gen-label">{t(genBusy[tx.key] ? 'max.generating' : 'max.generate')}</span></button>}
                            </span>
                          </label>
                          <div className="sm-multi">
                            {inputs.map((val, i) => (
                              <input key={i} className="sm-input" type="text" value={val}
                                maxLength={tx.max_len || undefined}
                                placeholder={i === vals.length ? t('max.addMore') : ''}
                                onChange={e => setMulti(tx.key, i, e.target.value)} />
                            ))}
                          </div>
                        </div>
                      );
                    }
                    const v = cTexts[tx.key] || '';
                    return (
                      <div className="sm-text" key={tx.key}>
                        <label className="sm-text-label">
                          <span>{inputLabel(tx.key, tx.label)}</span>
                          <span className="sm-text-right">
                            <span className="sm-text-hint">{tx.max_len ? `≤${tx.max_len}` : ''}</span>
                            {landingUrl.trim() && !genBlocked && <button type="button" className="sm-gen" disabled={!!genBusy[tx.key]}
                              onClick={e => { e.preventDefault(); genText(tx); }} title={t('max.generateFromPage')}>{genBusy[tx.key] ? <span className="sm-gen-spin" aria-hidden /> : <span className="sm-gen-spark" aria-hidden />}<span className="sm-gen-label">{t(genBusy[tx.key] ? 'max.generating' : 'max.generate')}</span></button>}
                          </span>
                        </label>
                        {(tx.max_len && tx.max_len >= 50) ? (
                          <textarea className="sm-input" rows={3} value={v} maxLength={tx.max_len || undefined}
                            onChange={e => setCTexts(p => ({ ...p, [tx.key]: e.target.value }))} />
                        ) : (
                          <input className="sm-input" type="text" value={v} maxLength={tx.max_len || undefined}
                            onChange={e => setCTexts(p => ({ ...p, [tx.key]: e.target.value }))} />
                        )}
                      </div>
                    );
                  };
                  const byKey: Record<string, any> = {};
                  creativeOpts.texts.forEach((t: any) => { byKey[t.key] = t; });
                  const keyFields = KEY_TEXTS.map(k => byKey[k]).filter(Boolean);
                  const extraFields = creativeOpts.texts.filter((t: any) => !HIDDEN_TEXTS.includes(t.key) && !KEY_TEXTS.includes(t.key));
                  return (
                    <>
                      <div className="sm-cr-group-title">{t('max.texts')}</div>
                      <div className="sm-texts">
                        {keyFields.map(renderText)}
                      </div>
                      {extraFields.length > 0 && (
                        <div className="sm-addtext">
                          <button type="button" className="sm-addtext-head" onClick={() => setShowMoreTexts(!showMoreTexts)}>
                            <span className="sm-addtext-title">{t('max.additionalText')}</span>
                            <svg className={`sm-addtext-chev ${showMoreTexts ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                          </button>
                          {showMoreTexts && <div className="sm-texts">{extraFields.map(renderText)}</div>}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Format previews — full catalog, fills live as materials are added */}
          <div className="form-section form-section-collapsible">
            <div className="form-section-header" onClick={() => setShowPreviews(!showPreviews)}>
              <h3 className="form-section-title">{t('max.formatPreviews')}</h3>
              <svg className={`form-section-chevron ${showPreviews ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </div>
            {showPreviews && catalog && (() => {
              const allF = catalog.platforms.flatMap((pl: any) =>
                ((catalog.by_platform && catalog.by_platform[pl.id]) || [])
                  .filter((f: any) => !isManage || launchedFormats.includes(f.id))   // manage: launched formats only
                  .map((f: any) => ({ ...f, _pl: pl.name })));
              const sel = allF.find((f: any) => f.id === selectedFmt) || allF[0];
              return (
                <div className="mk-wrap">
                  <div className="mk-master">
                    <div className="mk-detail">
                      {sel && (() => {
                        const missing = fmtMissing(sel);
                        return (
                          <>
                            <div className="mk-detail-head">{sel._pl} · {sel.name}</div>
                            <div className="mk-detail-cols">
                              <div className="mk-detail-body">{renderMockup(sel)}</div>
                              <div className="mk-detail-info">
                                {missing.length > 0 && (
                                  <div className="mk-missing"><span className="mk-missing-dot" />{t('max.missingLabel', { items: missing.join(', ') })}</div>
                                )}
                                {(sel.inputs || []).length > 0 && (
                                  <div className="mk-uses">
                                    <div className="mk-uses-title">{t('max.thisFormatUses')}</div>
                                    {sel.inputs.map((inp: any) => {
                                      const fits = inputFitsFormat(sel, inp.key);
                                      const val = inputDisplay(inp.key);
                                      const offSpec = !fits && val !== '—' && ASSET_INPUT_KEYS.has(inp.key);
                                      return (
                                        <div className={`mk-use-row ${fits ? 'on' : ''}`} key={inp.key}>
                                          <span className="mk-use-dot" />
                                          <span className="mk-use-label">{inputLabel(inp.key, inp.label)}{inp.required && <span className="mk-use-req" title={t('max.required')}>*</span>}</span>
                                          <span className="mk-use-val">{val}{offSpec && t('max.offSpecShort')}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                {(sel.assets || []).filter((a: any) => a.type !== 'logo').length > 0 && (
                                  <div className="mk-uses" style={{ marginTop: 10 }}>
                                    <div className="mk-uses-title">{t('max.materialsCheckedAgainst', { platform: sel._pl })}</div>
                                    {(sel.assets || []).filter((a: any) => a.type !== 'logo').map((a: any, ai: number) => {
                                      const fit = assetFit(a);
                                      const need = a.count_min || 1;
                                      const enough = fit.pass.length >= need;
                                      return (
                                        <div key={ai} style={{ marginBottom: 6 }}>
                                          <div className={`mk-use-row ${enough ? 'on' : ''}`}>
                                            <span className="mk-use-dot" />
                                            <span className="mk-use-label">{assetLabel(a)}{a.required && <span className="mk-use-req" title={t('max.required')}>*</span>}{need > 1 && <span style={{ color: '#999' }}>{t('max.minCount', { count: need })}</span>}</span>
                                            <span className="mk-use-val">{enough
                                              ? t('max.nOk', { count: fit.pass.length })
                                              : (fit.pass.length > 0 ? `${fit.pass.length}/${need}`
                                                : (a.required ? (need > 1 ? t('max.nNeeded', { count: need }) : t('max.requiredShort')) : t('max.optional')))}</span>
                                          </div>
                                          {(fit.pass.length > 0 || fit.fail.length > 0 || fit.pending.length > 0) && (
                                            <div style={{ paddingLeft: 16, fontSize: 11, lineHeight: 1.5 }}>
                                              {fit.pass.map((it: any) => <div key={it.id} style={{ color: '#16794a' }}>✓ {it.name}</div>)}
                                              {/* A file that was uploaded but misses THIS platform's spec used to
                                                  vanish here, leaving a bare "required" — say what is wrong with it. */}
                                              {fit.fail.map(({ item, reason }: any) => (
                                                <div key={item.id} style={{ color: '#c0392b' }}>✗ {item.name}{reason ? ` — ${reason}` : ''}</div>
                                              ))}
                                              {fit.pending.map((it: any) => (
                                                <div key={it.id} style={{ color: '#999' }}>… {it.name} — {t('max.checkingSuffix')}</div>
                                              ))}
                                            </div>
                                          )}
                                          {assetSpec(a) && <div style={{ paddingLeft: 16, fontSize: 10, color: '#999' }}>{assetSpec(a)}</div>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="mk-list">
                      {catalog.platforms.map((pl: any) => {
                        const fmts = ((catalog.by_platform && catalog.by_platform[pl.id]) || [])
                          .filter((f: any) => !isManage || launchedFormats.includes(f.id));   // manage: launched only
                        if (!fmts.length) return null;
                        return (
                          <div key={pl.id}>
                            <div className="mk-list-plat">{pl.name}</div>
                            {fmts.map((f: any) => {
                              const fill = fmtFill(f);
                              const gate = fmtGate(f);
                              const st = gate ? 'empty' : (fmtReady(f) ? 'ready' : fill > 0 ? 'partial' : 'empty');
                              const off = disabledFormats.has(f.id) || !!gate;
                              // Toggle only on launch (not manage mode), only for a ready, non-gated format.
                              const showSw = !isManage && !gate && st === 'ready';
                              const locked = showSw && !off && toggleLocked(pl.id, f.id);
                              return (
                                <button type="button" key={f.id}
                                  className={`mk-list-row ${sel && sel.id === f.id ? 'sel' : ''} ${off ? 'off' : ''} ${gate ? 'gated' : ''}`}
                                  title={gate || undefined}
                                  onClick={() => setSelectedFmt(f.id)}>
                                  {showSw ? (
                                    <span
                                      className={`mk-sw ${off ? '' : 'on'} ${locked ? 'locked' : ''}`}
                                      role="switch" aria-checked={!off}
                                      title={locked ? t('max.keepOneEach')
                                        : t(off ? 'max.includeFormat' : 'max.excludeFormat')}
                                      onClick={(e) => { e.stopPropagation(); toggleFormat(pl.id, f.id); }}>
                                      <span className="mk-sw-knob" />
                                    </span>
                                  ) : <span className="mk-sw-spacer" />}
                                  <span className={`mk-dot ${st}`} />
                                  <span className="mk-list-name">{f.name}</span>
                                  {gate && <span className="mk-list-gate">{gate}</span>}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          </div>
        </div>
      </div>

      {showSetupModal && (
        <div
          onClick={() => setShowSetupModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,16,40,0.45)', display: 'flex',
                   alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, padding: '28px 28px 22px', maxWidth: 420,
                     width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'center' }}
          >
            <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 12 }}>⚙️</div>
            <h3 style={{ margin: '0 0 10px', fontSize: 19, fontWeight: 700, color: '#1b1430' }}>
              {t('max.setupIncomplete')}
            </h3>
            <p style={{ margin: '0 0 22px', color: '#555', fontSize: 14, lineHeight: 1.5 }}>
              {t('max.setupIncompleteText')}
            </p>
            <button
              className="btn-primary"
              onClick={() => setShowSetupModal(false)}
              style={{ minWidth: 120 }}
            >
              {t('max.gotIt')}
            </button>
          </div>
        </div>
      )}

      {confirmLaunch && (
        <div className="sm-confirm-overlay" onClick={() => setConfirmLaunch(false)}>
          <div className="sm-confirm" onClick={(e) => e.stopPropagation()}>
            <h3 className="sm-confirm-title">{t('max.confirmLaunchTitle')}</h3>
            <p className="sm-confirm-text">
              <Trans i18nKey="max.confirmLaunchText" components={{ b: <strong /> }} />
            </p>
            <div className="sm-confirm-actions">
              <button className="btn-secondary-sm" onClick={() => setConfirmLaunch(false)}>{t('common.cancel')}</button>
              <button className="btn-publish" disabled={launching}
                onClick={() => { setConfirmLaunch(false); handleLaunch(); }}>
                {launching ? (<><span className="btn-spinner"></span>{t('max.launchingShort')}</>) : t('max.launchBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {launchResult && (() => {
        const chans = launchResult.channels || {};
        const total = Object.keys(chans).length;
        const err = launchResult.error;
        const bal = err && typeof err === 'object' && err.code === 'insufficient_balance' ? err : null;
        // A blocking error with no per-platform results → nothing was created (the aggregate
        // balance preflight rejected the whole launch). Render our standard message-modal, in
        // our style, not the "Launched 🎉 · 0/0 created" success chrome.
        if (bal || (err && total === 0)) {
          const ccy = (bal && bal.currency) || currency || 'EUR';
          const money = (v: number) =>
            `${Number(v || 0).toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;
          const resuming = !!bal && bal.verb === 'resume';
          return (
            <div className="modal-overlay" onClick={() => setLaunchResult(null)}
              style={{ opacity: 1, visibility: 'visible', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="message-modal" onClick={(e) => e.stopPropagation()}>
                <div className="message-modal-icon error">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
                <h3>{t('max.notEnoughBalance')}</h3>
                {bal ? (
                  <>
                    <p>{t(resuming ? 'max.topUpToResume' : 'max.topUpToLaunch')}</p>
                    <div className="launch-charge-block">
                      <div className="launch-charge-label">{t(resuming ? 'max.neededToResume' : 'max.neededToLaunch')}</div>
                      <div className="launch-charge-value">{money(bal.need)}</div>
                      <div className="launch-balance-line">
                        {t('max.currentBalance')} <strong>{money(bal.have)}</strong>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="launch-warning launch-warning-error">{String(err)}</p>
                )}
                <div className="message-modal-actions">
                  <button className="message-modal-btn-secondary" onClick={() => setLaunchResult(null)}>{t('max.gotIt')}</button>
                </div>
              </div>
            </div>
          );
        }
        const ok = (launchResult.launched || []).length;
        const failed = Object.values(chans).filter((c: any) => ['FAILED', 'INCOMPLETE'].includes(c.status)).length;
        // Same rename as the catalog: our own DSP is the partner's product on a white-label.
        const label: Record<string, string> = { sense: whiteLabel ? `${branding.brand_name} DSP` : 'Sense', google_ads: 'Google Ads', meta: 'Meta', tiktok: 'TikTok', yandex: 'Yandex', linkedin: 'LinkedIn' };
        return (
          <div onClick={() => setLaunchResult(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(20,16,40,0.45)', display: 'flex',
                     alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 16, padding: '26px 26px 20px', maxWidth: 520,
                       width: '92%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 700, color: '#1b1430' }}>
                {t(failed ? 'max.launchIssues' : 'max.launchedOk')}
              </h3>
              <p style={{ margin: '0 0 16px', color: '#666', fontSize: 13 }}>
                {t('max.platformsCreated', { ok, total })}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Object.entries(chans).map(([ch, c]: [string, any]) => {
                  const good = ['READY', 'ACTIVE', 'DRAFT_CREATED'].includes(c.status);
                  return (
                    <div key={ch} style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 12px', borderRadius: 10, background: good ? '#f2fbf5' : '#fdf3f3',
                      border: `1px solid ${good ? '#c8ebd4' : '#f2d2d2'}` }}>
                      <span style={{ fontSize: 16 }}>{good ? '✅' : '⚠️'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1b1430' }}>
                          {label[ch] || ch}
                          <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}> · {c.status}</span>
                        </div>
                        {c.format && <div style={{ fontSize: 12, color: '#888' }}>{c.format}</div>}
                        {c.errors && c.errors.length > 0 && (
                          <div style={{ fontSize: 12, color: '#c0392b', marginTop: 3, wordBreak: 'break-word' }}>
                            {c.errors[0]}
                          </div>)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                <button className="btn-primary" onClick={() => { setLaunchResult(null); navigate('/campaigns'); }}
                  style={{ minWidth: 120 }}>{t('max.done')}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SenseMaxPage;
